import "core-js/modules/es.array.push.js";
import "core-js/modules/es.set.difference.v2.js";
import "core-js/modules/es.set.intersection.v2.js";
import "core-js/modules/es.set.is-disjoint-from.v2.js";
import "core-js/modules/es.set.is-subset-of.v2.js";
import "core-js/modules/es.set.is-superset-of.v2.js";
import "core-js/modules/es.set.symmetric-difference.v2.js";
import "core-js/modules/es.set.union.v2.js";
import "core-js/modules/esnext.iterator.constructor.js";
import "core-js/modules/esnext.iterator.every.js";
import "core-js/modules/esnext.iterator.filter.js";
import "core-js/modules/esnext.iterator.find.js";
import "core-js/modules/esnext.iterator.flat-map.js";
import "core-js/modules/esnext.iterator.for-each.js";
import "core-js/modules/esnext.iterator.map.js";
import "core-js/modules/esnext.iterator.reduce.js";
import "core-js/modules/esnext.iterator.some.js";
import { domesticModels, foreignModels } from '@/views/main/config/index.js';
// ── 从 useReportDataV2 合并的共享状态和工具函数 ──
import { ref, computed } from 'vue';
import { resetSectionRegistry } from './components/v3/composables/useSectionRegistry';
// ── 数据适配层 ──
const reportData = ref(null);
function setReportData(data) {
  resetSectionRegistry();
  reportData.value = data;
}
const meta = computed(() => reportData.value?.meta ?? null);
const platforms = computed(() => meta.value?.platforms ?? []);
// 七维展示元数据（颜色 / 权重）—— v3 新版从六维扩展为七维，新增 ES（证据支撑度）
const DIM_META = {
  BCR: {
    name: '品牌覆盖率',
    weight: 22,
    barColor: 'var(--geo-blue)',
    abbrColor: 'var(--geo-blue)'
  },
  MRR: {
    name: '推荐触发率',
    weight: 18,
    barColor: 'var(--geo-red)',
    abbrColor: 'var(--geo-red)'
  },
  ES: {
    name: '证据支撑度',
    weight: 15,
    barColor: '#722ED1',
    abbrColor: '#722ED1'
  },
  IA: {
    name: '信息准确率',
    weight: 13,
    barColor: '#4096FF',
    abbrColor: '#4096FF'
  },
  SS: {
    name: '情感倾向分',
    weight: 13,
    barColor: '#1677FF',
    abbrColor: '#1677FF'
  },
  CWR: {
    name: '竞品胜出率',
    weight: 12,
    barColor: 'var(--geo-orange)',
    abbrColor: 'var(--geo-orange)'
  },
  SoV: {
    name: '声量份额',
    weight: 7,
    barColor: 'var(--geo-green)',
    abbrColor: 'var(--geo-green)'
  }
};
const DIM_ORDER = ['BCR', 'MRR', 'ES', 'IA', 'SS', 'CWR', 'SoV'];
// 意图分类字母码 → 中文分类名（keyword_coverage.categories[].display_name 目前恒等于 category 字母，无真实命名，故前端兜底映射）
const CATEGORY_LABEL_MAP = {
  A: '认知可见度',
  B: '推荐转化',
  C: '竞品决策',
  D: '内容可信',
  E: '品牌表达',
  F: '全链路稳定性',
  G: '监管合规'
};
// 维度是否「已测评」：null/undefined 视为未测评（隐藏），0 视为测评到的真实低分（正常展示）。
// 一致用于所有按维度展示分数的卡片/条形/KPI，避免出现「未测评」被悄悄当成「0分」展示的误导。
function isDimMeasured(v) {
  return v !== undefined && v !== null;
}
const KIND_COLOR = {
  red: 'var(--geo-red)',
  orange: 'var(--geo-orange)',
  blue: 'var(--geo-blue)',
  green: 'var(--geo-green)',
  gray: 'var(--geo-td)'
};
// 分值配色（产品确认，2026.07.07）：优秀/良好/中等/较差/差 五档各自独立色值，
// 与 gradeBands 的 80/60/40/20/0 分档一一对应，专供 gradeColor/geoScoreColor/scoreColor 这类
// 直接输出色值的场景；徽标类仍用 KIND_COLOR 三色（pill CSS 类只有 red/orange/green/gray/blue 几档）。
const GRADE_COLOR = {
  优秀: '#26CD77',
  良好: '#84D55A',
  中等: '#FEA91F',
  较差: '#FF7124',
  差: '#F53F3F'
};
// 推荐徽标/标签三套配色（rank 1/2/3）
// Top3 推荐徽标配色：按被推荐品牌自身的 GEO 分值三档取色（<40红/<60橙/其余绿），不再按排名固定配色
function recPalette(geo) {
  if (geo < 40) return {
    badgeBg: '#FF4D4F',
    chipBg: 'rgba(255,77,79,.10)',
    chipBorder: '1px solid rgba(255,77,79,.3)'
  };
  if (geo < 60) return {
    badgeBg: '#FAAD14',
    chipBg: 'rgba(250,173,20,.13)',
    chipBorder: '1px solid rgba(250,173,20,.3)'
  };
  return {
    badgeBg: '#16A34A',
    chipBg: 'rgba(22,163,74,.10)',
    chipBorder: '1px solid rgba(22,163,74,.3)'
  };
}
const STRIPE_PALETTE = ['#4F8EF7', '#1677FF', '#0FC6C2', '#F59E0B', '#9B59B6', '#FF4D4F'];
// 平台名统一以本地模型配置表为准：后端返回的平台名可能大小写不一致或夹杂符号（如「文心一言'」），
// 规范化（只留中英文数字、转小写）后按配置 label 匹配，命中则返回配置里的标准 label
// （封面图标等也按该 label 查配置表），未命中时回退后端原名
const ALL_MODELS = [...domesticModels, ...foreignModels];
function platformKeyOf(name) {
  return String(name ?? '').replace(/[^0-9a-z一-鿿]/gi, '').toLowerCase();
}
function canonPlatform(name) {
  const key = platformKeyOf(name);
  if (!key) return '';
  return ALL_MODELS.find(m => platformKeyOf(m.label) === key)?.label ?? String(name).trim();
}
// 占位/空值清洗：契约里 `【CONFIG】`/`【示例】`/`【LLM】` 等占位或空串视为「无数据」
function clean(v) {
  if (typeof v !== 'string') return undefined;
  const s = v.trim();
  if (!s || s.includes('【')) return undefined;
  return s;
}
// data_period 前后日期一致时（如 "2026-07-14 ~ 2026-07-14"），回退使用 report_date
function resolveDataPeriod(meta) {
  const raw = meta.data_period ?? '';
  const m = /^(\d{4}-\d{2}-\d{2})\s*~\s*(\d{4}-\d{2}-\d{2})$/.exec(raw);
  if (m && m[1] === m[2] && meta.report_date) return meta.report_date;
  return raw;
}
function round1(n) {
  return Math.round((n ?? 0) * 10) / 10;
}
// AI 原话：新 schema 里 ai_quotes[] 是纯字符串 "[平台|置信]文本"，需要正则拆解
// （旧契约 ai_quotes[] 是 {text,platform,keyword} 对象，非字符串时直接回退空值，不再假设可 .slice）
function quoteMeta(q) {
  if (typeof q !== 'string') return {
    platform: '',
    confidence: '',
    text: ''
  };
  const m = /^\[([^|\]]+)\|(\d+)\]\s*([\s\S]*)$/.exec(q);
  return m ? {
    platform: m[1],
    confidence: m[2],
    text: m[3]
  } : {
    platform: '',
    confidence: '',
    text: q
  };
}
// 按 competitor_type 对非目标品牌分组（2.2 竞品范围 / 9.2 竞品说明 共用）
function groupByCompetitorType(brands) {
  const map = new Map();
  (brands ?? []).filter(b => !b.is_target).forEach(b => {
    const key = b.competitor_type || '未分类';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(b.name);
  });
  return Array.from(map.entries()).map(([type, names]) => ({
    type,
    names
  }));
}
// 七维定义 + 计算公式（前端模板 + 品牌名插值，1.1 sevenDims 与 9.1 rows 共用）
// cwr 明细文案是否显示"胜负明细缺失"由调用方传入真实校验结果（cwrDetailMissing），不在本函数内假设
function sevenDimDefs(brand, cwr) {
  const b = brand || '本品';
  const cwrDetailText = cwr.missing ? '胜负明细缺失，口径D5' : `胜${cwr.win}负${cwr.lose}平${cwr.tie}`;
  return [{
    dim: 'BCR',
    name: DIM_META.BCR.name,
    definition: `在品类相关问题中，AI回答主动提及「${b}」的比例（主动推荐+条件推荐计为品牌被构建）。被主动提及是一切转化链路的前提。`,
    formula: 'BCR = 品牌构建回答数 / 有效回答数 × 100'
  }, {
    dim: 'MRR',
    name: DIM_META.MRR.name,
    definition: `${b}在AI推荐类问答中被具体推荐的比例，是最接近购买转化的指标。`,
    formula: 'MRR = 被推荐次数 / 推荐类响应数 × 100'
  }, {
    dim: 'ES',
    name: DIM_META.ES.name,
    definition: `AI回答中对「${b}」事实断言的可溯源证据质量。含数据型、来源型、奖项型、官方型四类证据；受监管行业存在强制降级。证据越强，AI输出的信息可信度越高。`,
    formula: 'ES = 加权证据记录数 / 有效回答记录数，经质量降级校准'
  }, {
    dim: 'IA',
    name: DIM_META.IA.name,
    definition: `AI引用${b}信息中准确信息的比例。信息空白期幻觉风险高：归属误标、规格混淆、价格幻觉等。`,
    formula: 'IA = 准确条目 / 核对总条目 × 100'
  }, {
    dim: 'SS',
    name: DIM_META.SS.name,
    definition: `AI描述${b}时的情感极性（100=完全正面，50为中性点，40-60为中性带）。`,
    formula: 'SS = (正面词频 − 负面词频) / 总词频，线性折算至0-100'
  }, {
    dim: 'CWR',
    name: DIM_META.CWR.name,
    definition: `${b} vs 竞品对比场景中AI倾向本品的比例（本轮共${cwr.count ?? 0}次对战，${cwrDetailText}）。`,
    formula: 'CWR = AI倾向本品次数 / 对比场景响应数 × 100'
  }, {
    dim: 'SoV',
    name: DIM_META.SoV.name,
    definition: `AI回答中${b}被提及次数占所有品牌总提及次数的比例，反映品类AI话语份额。`,
    formula: 'SoV = 本品提及次数 / 所有品牌提及总次数 × 100'
  }];
}
export function useReportV3ViewModel() {
  // reportData 现在是模块级共享状态
  // ── 评级映射：新契约档位 80/60/40/20（优秀/良好/中等/较差/差），与 geo_formula.grade_rules 口径一致 ──
  const gradeBands = computed(() => {
    const bands = reportData.value?.rubric?.geo_grade_bands;
    if (Array.isArray(bands) && bands.length) {
      return [...bands].sort((a, b) => b.min - a.min);
    }
    return [{
      min: 80,
      grade: '优秀'
    }, {
      min: 60,
      grade: '良好'
    }, {
      min: 40,
      grade: '中等'
    }, {
      min: 20,
      grade: '较差'
    }, {
      min: 0,
      grade: '差'
    }];
  });
  function gradeOf(score) {
    return gradeBands.value.find(b => score >= b.min)?.grade ?? '差';
  }
  // 评级 → pill 徽标三色（CSS 类只有 red/orange/green/gray 几档，优秀/良好共用绿、较差/差共用红）。
  // 仅供徽标背景/标签类用；分值本身的色值走下面 gradeColor/geoScoreColor/scoreColor（五档 GRADE_COLOR）。
  function kindOfGrade(grade) {
    return {
      优秀: 'green',
      良好: 'green',
      中等: 'orange',
      较差: 'red',
      差: 'red'
    }[grade] ?? 'gray';
  }
  function kindOfScore(score) {
    return kindOfGrade(gradeOf(score));
  }
  // GEO 综合得分（0-100 总分）在封面/1.1/3.1/3.3/4.1 各处统一取色的唯一入口：
  // 评级 → 色值，全部走同一份 rubric 分档 + GRADE_COLOR 五档色值，不要在各 computed 里各自拼色。
  function gradeColor(grade) {
    return GRADE_COLOR[grade] ?? KIND_COLOR.gray;
  }
  function geoScoreColor(score) {
    return gradeColor(gradeOf(score));
  }
  // 裸数值（旁边没有评级文字，如七维得分条/大字）与 geoScoreColor 同一套五档色值——
  // GRADE_COLOR 五档已经能区分 22 分（较差）和 57 分（中等），不再需要单独的三段色。
  function scoreColor(score) {
    return geoScoreColor(score);
  }
  // 命中档位的区间文案，如「评级区间：<20分」
  function bandRangeText(score) {
    const sorted = gradeBands.value;
    const idx = sorted.findIndex(b => score >= b.min);
    const cur = sorted[idx];
    const upper = sorted[idx - 1];
    if (!upper) return `评级区间：≥${cur.min}分`;
    if (cur.min === 0) return `评级区间：<${upper.min}分`;
    return `评级区间：${cur.min}-${upper.min - 1}分`;
  }
  function coverageColor(pct) {
    if (pct <= 0) return 'var(--geo-red)';
    if (pct < 50) return 'var(--geo-orange)';
    return 'var(--geo-green)';
  }
  const problemByCode = computed(() => {
    const map = {};
    (reportData.value?.problem_diagnosis ?? []).forEach(p => map[p.code] = p);
    return map;
  });
  const platformNames = computed(() => (reportData.value?.meta?.platforms ?? []).map(p => canonPlatform(p.display_name ?? p.key)).filter(Boolean));
  const brandsSorted = computed(() => {
    const list = reportData.value?.brand_stats;
    if (!Array.isArray(list)) return [];
    return [...list].sort((a, b) => (a.rank_by_geo ?? 99) - (b.rank_by_geo ?? 99));
  });
  const targetBrandRow = computed(() => brandsSorted.value.find(b => b.is_target));
  const categoryTop1 = computed(() => brandsSorted.value[0]);
  const top1Competitor = computed(() => brandsSorted.value.find(b => !b.is_target));
  const competitorList = computed(() => brandsSorted.value.filter(b => !b.is_target).slice(0, 8));
  const selectedCompetitorIdx = ref(0);
  const selectedCompetitor = computed(() => competitorList.value[selectedCompetitorIdx.value] ?? top1Competitor.value);
  function selectCompetitor(idx) {
    if (idx >= 0 && idx < competitorList.value.length) {
      selectedCompetitorIdx.value = idx;
    }
  }
  // CWR 胜负明细（win+lose+tie）与 cwr_count 对不上时视为"明细缺失"——全文件唯一真实校验，
  // 所有需要判断"CWR明细是否缺失"的地方都应引用这个共享值，不要各自用 k === 'CWR' 当替身判断
  const cwrDetailMissing = computed(() => {
    const t = reportData.value?.target_overview ?? {};
    return (t.cwr_win ?? 0) + (t.cwr_lose ?? 0) + (t.cwr_tie ?? 0) !== (t.cwr_count ?? 0);
  });
  // sevenDimDefs() 的 cwr 参数装配，供 1.1 / 9.1 两处调用共用
  function cwrInfoOf(t) {
    return {
      count: t.cwr_count ?? 0,
      missing: cwrDetailMissing.value,
      win: t.cwr_win ?? 0,
      lose: t.cwr_lose ?? 0,
      tie: t.cwr_tie ?? 0
    };
  }
  function rankColor(rank) {
    if (rank <= 1) return 'var(--geo-green)';
    if (rank <= 2) return 'var(--geo-blue)';
    if (rank <= 3) return 'var(--geo-orange)';
    return 'var(--geo-td)';
  }
  const PRIORITY_META = {
    P0: {
      kind: 'geo-pill-red',
      pill: 'red',
      label: '立即干预',
      variant: ''
    },
    P1: {
      kind: 'geo-pill-orange',
      pill: 'orange',
      label: '本月响应',
      variant: 'p1'
    },
    P2: {
      kind: 'geo-pill-blue',
      pill: 'blue',
      label: '持续观察',
      variant: 'p2'
    }
  };
  const DIM_SPAN_COLOR = {
    BCR: 'var(--geo-blue)',
    MRR: 'var(--geo-red)',
    ES: '#722ED1',
    IA: '#4096FF',
    SS: '#1677FF',
    CWR: 'var(--geo-orange)',
    SoV: 'var(--geo-green)'
  };
  const CIRCLED = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩', '⑪', '⑫', '⑬', '⑭', '⑮'];
  // ── Cover ──
  const coverProps = computed(() => {
    const d = reportData.value;
    if (!d) return undefined;
    const meta = d.meta ?? {};
    const t = d.target_overview ?? {};
    const brands = brandsSorted.value;
    const top1 = categoryTop1.value;
    const platforms = platformNames.value;
    const NP = platforms.length;
    const geo = t.GEO ?? 0;
    const grade = t.geo_grade || gradeOf(geo);
    const gap = top1 ? Math.max(0, round1((top1.GEO ?? 0) - geo)) : 0;
    const roadmapLen = (d.action_roadmap?.stages ?? []).length;
    const rank = targetBrandRow.value?.rank_by_geo ?? brands.length;
    const brand = clean(meta.target_brand) ?? '';
    const facts = (t.extracted_facts_summary ?? []).map(f => clean(f)).filter(Boolean);
    const specBar = facts.length ? facts.slice(0, 8).map(f => ({
      value: '实测事实',
      label: f,
      tone: 'gold'
    })) : [{
      value: '—',
      label: '本轮未提取到结构化产品事实',
      tone: ''
    }];
    const descHtml = platforms.length ? `${clean(meta.launch_description) ? `${meta.launch_description} ` : ''}本报告基于 ${platforms.join('、')} ${NP}个主流AI大模型平台的实测数据，监测周期 ${resolveDataPeriod(meta)}，共执行 <strong style="color:var(--geo-gold)">${meta.total_keywords ?? 0}条咨询词 × ${NP}平台 × ${meta.monitor_rounds ?? 1}轮 = ${meta.total_answers ?? 0}次AI响应分析</strong>（获取品牌 ${meta.total_records ?? 0} 个），对${brand}当前的AI品牌可见度、信息准确性、竞品格局与消费旅程覆盖进行系统性量化诊断。` : '';
    const eyebrow = clean(meta.target_category) ? `${meta.target_category} · GEO 品牌AI可见度监测报告` : 'GEO 品牌AI可见度监测报告';
    const confidentialLine = `CONFIDENTIAL  ·  ${resolveDataPeriod(meta)}  ·  V1.1  ·  © Marketingforce`;
    return {
      eyebrow,
      confidentialLine,
      descHtml,
      specBar,
      titleBrand: brand,
      titleModel: '',
      platforms: platforms.length ? platforms : undefined,
      score: {
        label: 'GEO综合可见度',
        num: String(geo),
        // 统一走 gradeColor()，与 3.1 综合表现、3.3 平台卡、4.1 排行表同一套评级色值
        color: gradeColor(grade),
        den: `/ 100 · ${NP || '多'}个AI平台加权`,
        // "评级 X" 文字色跟着实际评级走（与得分数字同一套 gradeColor），不再固定红色；
        // "差距 N分" 语义上始终是"落后"，保持红色
        noteHtml: `评级 <strong style="color:${gradeColor(grade)}">${grade}</strong>${top1 ? top1.is_target ? `<br>当前位居品类第一（${top1.GEO}分）` : `<br>距品类第一（${top1.name}·${top1.GEO}分）差距 <strong style="color:var(--geo-red)">${gap}分</strong>` : ''}<br>修复路径已收敛为${roadmapLen}条主线 · 见 <strong style="color:var(--geo-gold)">第08章 行动方向预览</strong>`
      },
      kpis: ['BCR', 'MRR', 'SS'].filter(k => isDimMeasured(t[k])).map(k => ({
        value: String(t[k]),
        tone: t[k] < 25 ? 'red' : '',
        labelHtml: `${k}均值<br>${DIM_META[k].name}`
      })).concat([{
        value: `第${rank}名`,
        tone: 'red',
        labelHtml: `当前品类排名<br>共${brands.length}个品牌`
      }]),
      metaItems: [{
        val: resolveDataPeriod(meta),
        lbl: '基准监测周期'
      }, {
        val: `${NP}个AI平台`,
        lbl: '监测范围'
      }, {
        val: `${brands.length}个品牌`,
        lbl: '同轮对标集合'
      }, {
        val: '商业机密',
        lbl: '保密等级',
        color: 'var(--geo-red)'
      }]
    };
  });
  // ── CH1.1 核心发现总览 ──
  const coreFindingsProps = computed(() => {
    const d = reportData.value;
    if (!d) return undefined;
    const t = d.target_overview ?? {};
    const brands = brandsSorted.value;
    const geo = t.GEO ?? 0;
    const grade = t.geo_grade || gradeOf(geo);
    const rank = targetBrandRow.value?.rank_by_geo ?? brands.length;
    const diags = d.problem_diagnosis ?? [];
    const p0n = diags.filter(x => x.priority === 'P0').length;
    const stats = [
    // 与封面/3.1综合表现/3.3平台卡/4.1排行表同一套评级色值，不再固定用蓝色
    {
      value: String(geo),
      tone: kindOfGrade(grade),
      labelHtml: `GEO综合均分 / 100<br>${grade}评级`
    }, {
      value: `第${rank}名`,
      tone: 'red',
      labelHtml: `${brands.length}个品牌品类AI排名`
    },
    // MRR 未测评时不展示"缺席率"（该值由 MRR 折算而来，MRR 缺失时无意义）
    ...(isDimMeasured(t.MRR) ? [{
      value: `${round1(100 - t.MRR)}%`,
      tone: 'red',
      labelHtml: '推荐场景缺席率<br>1 − MRR/100'
    }] : []), {
      value: `${diags.length}项`,
      tone: 'red',
      labelHtml: `P0/P1/P2诊断问题<br>需分级响应（P0×${p0n}）`
    }];
    const sevenDims = sevenDimDefs(clean(d.meta?.target_brand) ?? '', cwrInfoOf(t)).map(x => ({
      abbr: x.dim,
      name: x.name,
      desc: x.definition,
      color: DIM_META[x.dim]?.barColor ?? 'var(--geo-blue)'
    }));
    const metricCards = ['BCR', 'MRR', 'ES', 'IA', 'SS'].filter(k => isDimMeasured(t[k])).map(k => {
      const v = t[k];
      const kind = kindOfScore(v);
      return {
        name: `${k} ${DIM_META[k].name}`,
        value: String(v),
        valueColor: scoreColor(v),
        sub: `权重 ${DIM_META[k].weight}%`,
        pillKind: kind,
        pillLabel: gradeOf(v)
      };
    });
    const negs = (t.negative_phrases ?? []).map(p => p.phrase).filter(Boolean);
    const worstDims = DIM_ORDER.filter(k => k !== 'CWR' && isDimMeasured(t[k])).sort((a, b) => t[a] - t[b]);
    const stages = d.funnel?.stages ?? [];
    const allZero = stages.length > 0 && stages.every(s => (s.coverage_rate_pct ?? 0) <= 0);
    const avgNeg = d.bns_analysis?.avg_negative_rate ?? 0;
    const riskHtml = `<strong>风险提示：</strong>本轮负面提及率 ${avgNeg}%，检出 ${negs.length} 条低频负面表述${negs.length ? `（${negs.join('；')}）` : ''}。最薄弱维度为 ${worstDims[0]}（${t[worstDims[0]] ?? 0}）与 ${worstDims[1]}（${t[worstDims[1]] ?? 0}）${allZero ? `，全部 ${stages.length} 个消费旅程阶段覆盖率均为 0，属全链路缺席（详见5.4）` : ''}。`;
    const meta = d.meta ?? {};
    const names = platformNames.value;
    const subTitle = `${names.length}个AI平台 · ${meta.total_keywords ?? 0}条咨询词 · ${meta.total_answers ?? 0}次响应 · ${resolveDataPeriod(meta)}基准监测`;
    return {
      subTitle,
      stats,
      sevenDims,
      metricCards,
      riskHtml
    };
  });
  // ── CH1.2 与友商头部差距速览 ──
  const competitorGapProps = computed(() => {
    const d = reportData.value;
    const stats = brandsSorted.value;
    if (!stats.length) return undefined;
    const t = d.target_overview ?? {};
    const targetIdx = stats.findIndex(b => b.is_target);
    // 展示口径：TOP8 竞品（按序位，非真实rank_by_geo）+ 监测主体固定追加在末尾（用真实排名第RANK名）
    const slots = competitorList.value.map((b, i) => ({
      n: `${CIRCLED[i] ?? i + 1}${i < 3 ? ' 目标区' : ''}`,
      b: b.name,
      kind: i < 3 ? 'tgt' : ''
    }));
    if (targetBrandRow.value) {
      slots.push({
        n: `第${targetIdx + 1} 当前`,
        b: `${targetBrandRow.value.name}★`,
        kind: 'me'
      });
    }
    const rivalBrand = selectedCompetitor.value ?? {};
    const dimRows = ['BCR', 'MRR', 'ES', 'CWR', 'SoV', 'IA'].map(k => {
      const me = t[k] ?? 0;
      const rival = k === 'CWR' ? me : rivalBrand[k] ?? 0;
      return {
        name: `${k} ${DIM_META[k].name}`,
        meValue: String(me),
        meColor: KIND_COLOR.red,
        rivalValue: k === 'CWR' ? '-' : String(rival),
        rivalColor: KIND_COLOR.green,
        dotLeft: `${Math.min(me, 100)}%`,
        linkWidth: `${Math.max(rival - me, 0)}%`,
        thrLeft: `${Math.min(rival, 100)}%`,
        gap: k === 'CWR' ? cwrDetailMissing.value ? '明细缺失' : '-' : `${rival - me >= 0 ? '-' : '+'}${round1(Math.abs(rival - me))}pp`
      };
    });
    const targetBrand = clean(d.meta?.target_brand) ?? '目标品牌';
    const rivalName = rivalBrand.name ?? '友商头部';
    return {
      ranking: {
        name: 'GEO综合排名',
        sub: `${stats.length}个监测品牌 · 实测排序`,
        slotsTitle: `前3名为监测目标区，${targetBrand}当前位于第${targetIdx + 1}`,
        slots,
        gap: `-${targetIdx}位`
      },
      dimRows,
      selectedSlotIndex: selectedCompetitorIdx.value,
      selectedCompetitorName: rivalName,
      noteHtml: `注：进度轴为<strong>0-100真实刻度</strong>，绿色标记为「${rivalName}」同轮实测各维值；点击排名条可切换对比竞品。${cwrDetailMissing.value ? 'CWR因胜负明细缺失不列差距。' : ''}`
    };
  });
  // ── CH1.3 本轮监测结论 ──
  const monitoringConclusionsProps = computed(() => {
    const d = reportData.value;
    if (!d) return undefined;
    const t = d.target_overview ?? {};
    const brand = clean(d.meta?.target_brand) ?? '本品';
    const top1 = top1Competitor.value;
    const platforms = d.target_by_platform ?? [];
    const bestPlat = platforms.length ? platforms.reduce((m, p) => p.GEO > m.GEO ? p : m, platforms[0]) : undefined;
    const avgNeg = d.bns_analysis?.avg_negative_rate ?? 0;
    const negs = (t.negative_phrases ?? []).map(p => p.phrase).filter(Boolean);
    const posPhrases = (t.positive_phrases ?? []).map(p => p.phrase).filter(Boolean);
    const strengths = [`正面叙事资产在位 - 检出「${posPhrases.slice(0, 3).join('、') || '—'}」等 ${t.positive_phrases?.length ?? 0} 条正面短语（6.2）`, bestPlat ? `平台突破口明确 - ${canonPlatform(bestPlat.platform)}（GEO ${bestPlat.GEO}，评级${bestPlat.geo_grade}）是当前表现最好的平台，是建设的优先入口（3.3）` : '', `声誉底盘可控 - 全平台平均负面率 ${avgNeg}%，检出 ${negs.length} 条低频负面表述，纠偏成本可控（6.1）`, `高频提及基础 - 品类语料中共被提及 ${t.total_mention ?? 0} 次，认知原点已存在（3.1）`].filter(Boolean);
    const gaps = (d.problem_diagnosis ?? []).map(x => `${x.label} - ${x.detail}（7.1之${x.priority}）`);
    if (!strengths.length && !gaps.length) return undefined;
    const funnel = d.funnel ?? {};
    const summaryHtml = clean(d.llm_generated?.exec_conclusion) ?? `${clean(funnel.insight_text) ?? ''}<br>${clean(funnel.breakthrough_text) ?? ''}`;
    return {
      columns: [{
        cardTitle: '现有可用资产 · 修复的起点',
        accent: 'var(--geo-green)',
        mark: '✓',
        items: strengths
      }, {
        cardTitle: '核心缺口 · 修复的对象',
        accent: 'var(--geo-red)',
        mark: '✗',
        items: gaps
      }],
      summaryHtml
    };
  });
  // ── CH1.4 商业影响解读 ──
  const businessImpactProps = computed(() => {
    const d = reportData.value;
    if (!d) return undefined;
    const t = d.target_overview ?? {};
    const fr = d.factual_risk ?? {};
    const bi = d.llm_generated?.biz_impact ?? {};
    const brand = clean(d.meta?.target_brand) ?? '本品';
    const top1 = top1Competitor.value;
    const categoryTop = categoryTop1.value;
    const stages = d.funnel?.stages ?? [];
    const stagesLen = stages.length;
    // 之前这里只判断"有没有旅程阶段"（stagesLen 真值），不管覆盖率实际多高都显示"偏低"；
    // 改成如实展示各阶段覆盖率均值，不再凭空下结论
    const avgStageCoverage = stagesLen ? round1(stages.reduce((s, x) => s + (x.coverage_rate_pct ?? 0), 0) / stagesLen) : 0;
    const gap = categoryTop ? Math.max(0, round1((categoryTop.GEO ?? 0) - (t.GEO ?? 0))) : 0;
    const sd = fr.severity_distribution ?? {};
    const accents = ['var(--geo-red)', 'var(--geo-orange)', 'var(--geo-red)'];
    const cards = [{
      title: '推荐缺席 ＝ AI渠道线索断流',
      kv: `MRR ${t.MRR ?? 0} · 全部${stagesLen}个旅程阶段覆盖率均值${avgStageCoverage}%`,
      body: clean(bi.absence) ?? `在AI上提出推荐类问题的，是购买意图最强的用户。当前${brand}在消费旅程阶段的覆盖仍有明显缺口，这部分本可转化的线索正持续流向${top1?.name ?? '头部竞品'}等竞品。`
    }, {
      title: '信息幻觉 ＝ 转化信任成本',
      kv: `FRR ${fr.frr_rate_pct ?? 0}% · ${fr.flawed_claims ?? 0}/${fr.total_claims ?? 0}条断言存在缺陷`,
      body: clean(bi.hallucination) ?? `本轮${fr.total_claims ?? 0}条AI事实断言中${fr.flawed_claims ?? 0}条存在缺陷（高严重度${sd['高'] ?? 0}条），错误口径直接抬高决策门槛，纠偏成本转嫁一线销售（详见6.5）。`
    }, {
      title: '内耗分流 ＝ 营销费用外溢',
      kv: categoryTop ? categoryTop.is_target ? `当前位居品类第一 · GEO ${categoryTop.GEO}` : `品类第一${categoryTop.name} GEO ${categoryTop.GEO} · 领先${gap}分` : '暂无头部竞品数据',
      body: clean(bi.dilution) ?? `竞品在AI决策建议中占据主导${top1 ? `（${top1.name}凭高BCR/MRR建立推荐优势）` : ''}，${brand}传播投入唤起的品类兴趣在AI端被优先导向竞品。`
    }];
    const cardsOut = cards.map((c, i) => ({
      accent: accents[i % accents.length],
      title: c.title,
      bodyHtml: `<strong style="color:${accents[i % accents.length]};">${c.kv}</strong><br>${c.body}`
    }));
    const q = (t.ai_quotes ?? [])[0];
    const qm = q ? quoteMeta(q) : undefined;
    const quote = qm && qm.text ? {
      text: qm.text.slice(0, 200),
      srcHtml: `- ${canonPlatform(qm.platform) || 'AI平台'} 实测原话${qm.confidence ? `（置信${qm.confidence}）` : ''} · 更多实测原话证据见6.3`
    } : {
      text: '',
      srcHtml: ''
    };
    const geo = t.GEO ?? 0;
    const grade = t.geo_grade || gradeOf(geo);
    const avgNeg = d.bns_analysis?.avg_negative_rate ?? 0;
    const windowHtml = `<strong>⏳ 窗口期判断：</strong>基准期${geo}分、评级${grade}${categoryTop ? categoryTop.is_target ? '，当前位居品类第一' : `，距品类第一${gap}分` : ''}。当前平均负面率${avgNeg}%，是以较低成本建立正确认知与差异化占位的窗口期。行动方向预览见第08章。`;
    const introHtml = `<strong>为什么GEO关乎销量：</strong>AI问答正在成为用户获取信息、比对信息、形成候选清单的<strong>前置入口</strong>。用户在AI回答中看不到的品牌，大概率不会进入其后续的试驾与订购清单——GEO衡量的正是${brand}在这条新增决策链路上的"货架可见度"。本节将本轮三组最关键的监测数字翻译为对应的业务影响。`;
    return {
      cards: cardsOut,
      quote,
      introHtml,
      windowHtml
    };
  });
  // ── CH2.1 监测对象 ──
  const monitoringTargetProps = computed(() => {
    const d = reportData.value;
    if (!d) return undefined;
    const meta = d.meta ?? {};
    const t = d.target_overview ?? {};
    const brand = clean(meta.target_brand) ?? '';
    const CELL = 'padding:8px 12px;font-weight:600;';
    const CELL_PLAIN = 'padding:8px 12px;';
    const brandCard = {
      title: '品牌信息卡',
      rows: [{
        label: '品牌名称',
        valueHtml: brand,
        valueStyle: CELL
      }, {
        label: '监测周期',
        valueHtml: resolveDataPeriod(meta),
        valueStyle: CELL_PLAIN
      }, {
        label: '监测平台',
        valueHtml: platformNames.value.join(' / '),
        valueStyle: CELL_PLAIN
      }, {
        label: '对标集合',
        valueHtml: `${(d.brand_stats ?? []).length}个品牌（同轮同词库实测）`,
        valueStyle: CELL_PLAIN
      }, {
        label: '保密等级',
        valueHtml: '商业机密',
        valueStyle: 'padding:8px 12px;color:var(--geo-red);font-weight:600;'
      }]
    };
    const facts = (t.extracted_facts_summary ?? []).map(f => clean(f)).filter(Boolean);
    const specCard = {
      title: 'AI 实测事实提取（代产品参数卡）',
      specs: facts.length ? facts.slice(0, 6).map(f => ({
        value: '实测事实',
        valueColor: 'var(--geo-gold)',
        note: f,
        bg: 'var(--geo-gold-d)'
      })) : [{
        value: '—',
        valueColor: 'var(--geo-td)',
        note: '本轮未提取到结构化产品参数',
        bg: 'rgba(17,24,39,.03)'
      }]
    };
    return {
      title: `监测对象：${brand}`,
      subTitle: '品牌卡 + AI实测事实提取',
      brandCard,
      specCard
    };
  });
  // ── CH2.2 监测范围与竞品集合 ──
  const monitoringScopeProps = computed(() => {
    const d = reportData.value;
    if (!d) return undefined;
    const meta = d.meta ?? {};
    const names = platformNames.value;
    const execRows = [{
      label: '监测平台',
      valueHtml: names.join(' / ')
    }, {
      label: '监测词总量',
      valueHtml: `<strong>${meta.total_keywords ?? 0}条咨询词</strong>`
    }, {
      label: 'AI响应总量',
      valueHtml: `<strong>${meta.total_answers ?? 0}次</strong>（${meta.total_keywords ?? 0}条 × ${names.length}平台 × ${meta.monitor_rounds ?? 1}轮）· 获取品牌${meta.total_records ?? 0} 个`
    }, {
      label: '监测周期',
      valueHtml: `${resolveDataPeriod(meta)} · ${meta.monitor_rounds ?? 1}轮`
    }, {
      label: '数据留存',
      valueHtml: '逐轮原始AI响应全文、各维度评分明细全量归档，支持按词 / 平台追溯调阅'
    }];
    const rivalRows = groupByCompetitorType(d.brand_stats ?? []).map(g => ({
      label: g.type,
      valueHtml: `${g.names.slice(0, 6).join('、')}${g.names.length > 6 ? ` 等${g.names.length}个` : ''}`
    }));
    const brand = clean(meta.target_brand) ?? '本品';
    return {
      subTitle: `${names.length}个AI平台 · ${meta.total_keywords ?? 0}条咨询词 · 竞品范围定义`,
      execCard: {
        title: '监测执行总览',
        colItem: '项目',
        colContent: '内容',
        rows: execRows
      },
      rivalCard: rivalRows.length ? {
        title: '竞品范围定义',
        colType: '类型',
        colRival: '竞品',
        rows: rivalRows
      } : undefined,
      boundaryHtml: `<strong>平台选择、数据口径与报告边界：</strong>① 上述${names.length}平台组合覆盖国内主流AI对话产品的核心活跃用户规模与咨询场景，后续可按需扩展新兴平台；② 本报告中全部竞品分数均与${brand}<strong>同轮、同词库、同复核流程实测</strong>得出，非估算或引用值；③ 报告边界：本报告聚焦<strong>AI问答渠道（GEO）</strong>的品牌可见度与认知质量，不覆盖搜索引擎SEO、社交媒体运营、广告投放、线下渠道等其他营销阵地的表现评估。`
    };
  });
  // ── CH2.3 监测分析框架 ──
  const analysisFrameworkProps = computed(() => {
    const d = reportData.value;
    if (!d) return undefined;
    const meta = d.meta ?? {};
    const names = platformNames.value;
    const answers = meta.total_answers ?? 0;
    const brandsLen = (d.brand_stats ?? []).length;
    const stagesLen = (d.funnel?.stages ?? []).length;
    const diagsLen = (d.problem_diagnosis ?? []).length;
    const roadmapLen = (d.action_roadmap?.stages ?? []).length;
    return {
      dataBand: {
        tag: '数据底座',
        titleHtml: `${meta.total_keywords ?? 0}条监测词 × ${names.length}平台 × ${meta.monitor_rounds ?? 1}轮 ＝ ${answers}次实测响应`,
        subHtml: '全量留存 · 口径见第09章'
      },
      lenses: [{
        q: '现状几分？',
        titleHtml: '结果视角<br>GEO七维评分',
        scope: `七维 × ${names.length}平台逐项实测`,
        chapter: '第03章'
      }, {
        q: '输给谁？',
        titleHtml: `竞争视角<br>${brandsLen}品牌对标`,
        scope: '排行 / 平台热力 / PK / 叙事占位 / 画像',
        chapter: '第04章'
      }, {
        q: '输在哪些场景？',
        titleHtml: '场景视角<br>消费旅程链路',
        scope: `${meta.total_keywords ?? 0}词分类体系 · ${stagesLen}阶段覆盖链路`,
        chapter: '第05章'
      }, {
        q: '错在哪些事实？',
        titleHtml: '内容视角<br>口径与风险',
        scope: '声誉 / 叙事 / 引用源 / 准确性 / 幻觉五块核查',
        chapter: '第06章'
      }],
      diagBand: {
        tag: '',
        titleHtml: `<strong>诊断收敛：</strong>P0 / P1 / P2 分级问题清单 ＋ 根因归纳（${diagsLen}项 · 第07章）`,
        subHtml: ''
      },
      actionBand: {
        tag: '',
        titleHtml: `<strong>行动方向：</strong>${roadmapLen}大阶段 ＋ 运营闭环（第08章）`,
        subHtml: ''
      }
    };
  });
  // ── CH3.1 综合得分 ──
  const compositeScoreProps = computed(() => {
    const d = reportData.value;
    if (!d) return undefined;
    const t = d.target_overview ?? {};
    const geo = t.GEO ?? 0;
    const grade = t.geo_grade || gradeOf(geo);
    const kind = kindOfGrade(grade);
    // CWR 胜负明细（win+lose+tie）与 cwr_count 对不上时，不显示进度条/分数，改显示"共N次对战·明细缺失"
    // （真实校验见共享的 cwrDetailMissing computed，全文件复用，不在此处重复计算）
    const weightBars = DIM_ORDER.filter(k => t[k] !== undefined && t[k] !== null).map(k => {
      const v = t[k];
      const m = DIM_META[k];
      if (k === 'CWR' && cwrDetailMissing.value) {
        return {
          label: `${k} ${m.name} ×${m.weight}%`,
          value: `共${t.cwr_count ?? 0}次对战·明细缺失`,
          barWidth: '0%',
          barColor: m.barColor,
          valueColor: KIND_COLOR.gray
        };
      }
      return {
        label: `${k} ${m.name} ×${m.weight}%`,
        value: String(v),
        barWidth: `${Math.min(v, 100)}%`,
        barColor: m.barColor,
        valueColor: scoreColor(v)
      };
    });
    const bands = gradeBands.value;
    const gradeBandRows = bands.map((b, i) => {
      const upper = bands[i - 1];
      const range = !upper ? `≥${b.min}分` : b.min === 0 ? `<${upper.min}分` : `${b.min}-${upper.min - 1}分`;
      const isCurrent = geo >= b.min && (!upper || geo < upper.min);
      return {
        range,
        desc: b.grade,
        color: gradeColor(b.grade),
        bg: isCurrent ? 'rgba(17,24,39,.05)' : 'rgba(17,24,39,.03)',
        border: isCurrent ? `2px solid ${gradeColor(grade)}` : 'none',
        current: isCurrent || undefined,
        currentLabel: isCurrent ? '当前' : undefined
      };
    });
    const worst = DIM_ORDER.filter(k => k !== 'CWR').sort((a, b) => (t[a] ?? 0) - (t[b] ?? 0));
    const best = worst[worst.length - 1];
    const weaknessHtml = `<strong>主要短板：</strong>${worst[0]}（${t[worst[0]] ?? 0}）和${worst[1]}（${t[worst[1]] ?? 0}）是拉低总分的核心指标（合计权重${DIM_META[worst[0]].weight + DIM_META[worst[1]].weight}%）。${cwrDetailMissing.value ? `CWR共${t.cwr_count ?? 0}次对战、胜负明细缺失。` : ''}表现相对最好的维度为${best}（${t[best] ?? 0}）。`;
    return {
      score: {
        value: String(geo),
        denom: '/ 100 分',
        color: gradeColor(grade),
        pillKind: kind,
        pillLabel: grade,
        bandText: bandRangeText(geo)
      },
      weightBars,
      weightingNoteHtml: clean(t.geo_formula_detail) ? `加权说明：${t.geo_formula_detail}` : clean(d.geo_formula?.formula) ? `加权说明：${d.geo_formula.formula}` : '',
      gradeBands: gradeBandRows,
      weaknessHtml
    };
  });
  // ── CH3.2 七维拆解 ──
  const dimensionBreakdownProps = computed(() => {
    const d = reportData.value;
    if (!d) return undefined;
    const t = d.target_overview ?? {};
    const platforms = d.target_by_platform ?? [];
    const dims = DIM_ORDER.filter(k => isDimMeasured(t[k])).map(k => {
      const score = t[k] ?? 0;
      // 平台级 CWR 为负值时是"明细缺失"哨兵值，不直接展示原始负数
      const perPlatform = platforms.map(p => `${canonPlatform(p.platform)} ${k === 'CWR' && (p[k] ?? 0) < 0 ? '明细缺失' : p[k] ?? 0}`).join(' · ');
      const cwrNote = k === 'CWR' ? cwrDetailMissing.value ? `。共${t.cwr_count ?? 0}次对战，胜负明细缺失。` : `。共${t.cwr_count ?? 0}次对战，胜${t.cwr_win ?? 0}负${t.cwr_lose ?? 0}平${t.cwr_tie ?? 0}。` : '。';
      return {
        abbr: k,
        abbrColor: DIM_META[k]?.abbrColor ?? 'var(--geo-blue)',
        // 参考渲染脚本这里是 "pr" if t[k]<40 else "po" 的两档规则，不是5档评级色，
        // 避免和 3.1 裸数值一样出现"中等/较差"共用橙色、看不出差距的问题
        pillKind: score < 40 ? 'red' : 'orange',
        pillLabel: `${score}均分`,
        comment: `${DIM_META[k].name}，权重${DIM_META[k].weight}%。分平台：${perPlatform}${cwrNote}`
      };
    });
    // 未测评维度会被过滤隐藏，实际卡片数可能少于7个；标题按当轮真实测评数对应中文数字生成，避免"七维"与卡片数对不上
    const cnNums = '一二三四五六七八九十';
    const title = `${cnNums[dims.length - 1] ?? dims.length}维指标拆解`;
    return {
      title,
      dims
    };
  });
  // ── CH3.3 各平台得分卡 ──
  const platformCardsProps = computed(() => {
    const d = reportData.value;
    if (!d || !Array.isArray(d.target_by_platform)) return undefined;
    const rankingByPlatform = {};
    (d.platform_rankings ?? []).forEach(pr => {
      rankingByPlatform[canonPlatform(pr.platform)] = pr.ranking ?? [];
    });
    const platformInsight = d.llm_generated?.platform_insight ?? {};
    const platforms = d.target_by_platform.map((p, idx) => {
      const pfName = canonPlatform(p.platform);
      const geo = round1(p.GEO ?? 0);
      const grade = p.geo_grade || gradeOf(geo);
      const recs = (rankingByPlatform[pfName] ?? []).filter(r => r.rank <= 3).map(r => ({
        rank: String(r.rank),
        name: `${r.name}（GEO ${r.GEO ?? 0}）`,
        ...recPalette(r.GEO ?? 0)
      }));
      const pDims = DIM_ORDER.filter(k => isDimMeasured(p[k]) && !(k === 'CWR' && p[k] < 0));
      const pBest = pDims.reduce((m, k) => (p[k] ?? 0) > (p[m] ?? 0) ? k : m, pDims[0]);
      const pWorst = pDims.reduce((m, k) => (p[k] ?? 0) < (p[m] ?? 0) ? k : m, pDims[0]);
      const top3 = rankingByPlatform[pfName] ?? [];
      let traitRule = `当前平台表现特征：${pBest}得分最高（${p[pBest] ?? 0}），${pWorst}最弱（${p[pWorst] ?? 0}）`;
      if (top3.length) traitRule += `；平台第一为${top3[0].name}（GEO ${top3[0].GEO}），本品该平台评级${p.geo_grade}`;
      const featureText = clean(platformInsight[p.platform]) ?? `${traitRule}。`;
      return {
        name: pfName,
        org: '',
        stripeColor: STRIPE_PALETTE[idx % STRIPE_PALETTE.length],
        score: String(Math.round(geo)),
        scoreClass: geo >= 55 ? 'high' : geo >= 35 ? 'mid' : 'low',
        // 与封面/3.1综合表现/4.1排行表同一套评级色值(按 rubric 评级分档)，不用独立的三段色
        scoreColor: gradeColor(grade),
        denom: '/100',
        pillKind: kindOfGrade(grade),
        pillLabel: grade,
        // 平台级 CWR 为负值是"明细缺失"哨兵值，不当作真实分数展示/参与宽度计算；
        // 维度本身未测评（null/undefined）时整条不展示，不与"测评到0分"混淆
        bars: DIM_ORDER.filter(k => isDimMeasured(p[k])).map(k => {
          const v = p[k];
          const missing = k === 'CWR' && v < 0;
          return {
            label: `${k} ${DIM_META[k].weight}%`,
            value: missing ? '明细缺失' : String(v),
            barWidth: missing ? '0%' : `${Math.min(Math.max(v, 0), 100)}%`,
            barColor: DIM_META[k].barColor,
            valueColor: missing ? KIND_COLOR.gray : scoreColor(v)
          };
        }),
        featureText,
        recTitle: '该平台优先推荐 Top 3',
        recommendations: recs
      };
    });
    return {
      subTitle: `${platforms.length}张平台详细得分卡 · 各平台AI表现特征 · 代表性AI原话`,
      platforms
    };
  });
  // ── CH4.1 品牌 GEO 竞争排行 ──
  const brandRankingProps = computed(() => {
    const d = reportData.value;
    const sorted = brandsSorted.value;
    if (!sorted.length) return undefined;
    const t = d.target_overview ?? {};
    const top1 = categoryTop1.value;
    const geo = t.GEO ?? 0;
    const brand = clean(d.meta?.target_brand) ?? '';
    // 表格口径：TOP9 竞品（按 rank_by_geo）+ 监测主体本身（即使主体不在前9名，也固定补一行在末尾）
    const top9Competitors = sorted.filter(b => !b.is_target).slice(0, 9);
    const targetRowData = targetBrandRow.value;
    const tableRows = targetRowData ? [...top9Competitors, targetRowData] : top9Competitors;
    const rows = tableRows.map(b => {
      const grade = b.geo_grade || gradeOf(b.GEO ?? 0);
      const rnk = b.rank_by_geo ?? 0;
      return {
        rank: CIRCLED[rnk - 1] ?? String(rnk),
        brandHtml: b.is_target ? `<strong style="color:var(--geo-gold);">${b.name} ★</strong>` : `<strong>${b.name}</strong>`,
        type: b.competitor_type ?? '',
        typeStyle: String(b.competitor_type ?? '').includes('内耗') ? 'color:var(--geo-red);font-weight:600;' : 'color:var(--geo-td);',
        bcr: String(b.BCR ?? 0),
        mrr: String(b.MRR ?? 0),
        es: String(b.ES ?? 0),
        // brand_stats[] 不带逐品牌 CWR 字段（仅 target_overview 单独存），4.1 排行表 CWR 列统一标"-"
        cwr: '-',
        sov: String(b.SoV ?? 0),
        geo: String(b.GEO ?? 0),
        geoColor: gradeColor(grade),
        grade,
        gradeKind: kindOfGrade(grade),
        rowStyle: b.is_target ? 'background:rgba(255,77,79,.06);' : undefined
      };
    });
    const gap = top1 ? Math.max(0, round1((top1.GEO ?? 0) - geo)) : 0;
    const rank = targetBrandRow.value?.rank_by_geo ?? sorted.length;
    // 差距叙述句优先用 category_benchmark.gap_text（数据侧/管线已算好的真实句子），前端只在其为空时兜底自算
    const gapText = clean(d.category_benchmark?.gap_text)?.replace(/[。.]+$/, '');
    const insightHtml = top1 ? top1.is_target ? `<strong>核心表现：</strong>${brand}（${geo}分）当前位居品类第一。当前排名第${rank}/${sorted.length}。` : `<strong>核心差距：</strong>${gapText ?? `${brand}（${geo}分）距品类第一${top1.name}（${top1.GEO}分）差距${gap}分`}。当前排名第${rank}/${sorted.length}。头部竞品凭 BCR ${top1.BCR ?? 0} / MRR ${top1.MRR ?? 0} 建立AI推荐优势（本品 BCR ${t.BCR ?? 0} / MRR ${t.MRR ?? 0}）。` : '';
    // TOP10 品牌跨平台提及分布：按 total_mention 取前10，逐平台拆出 mention 次数做堆积条形图（与GEO综合分无关）
    const names = platformNames.value;
    const top10 = [...sorted].sort((a, b) => (b.total_mention ?? 0) - (a.total_mention ?? 0)).slice(0, 10);
    const chartBrands = top10.map(b => b.is_target ? `${b.name} ★` : b.name);
    const chartSeries = names.map(pf => ({
      name: pf,
      data: top10.map(b => (b.by_platform ?? []).find(x => canonPlatform(x.platform) === pf)?.mention ?? 0)
    }));
    return {
      subTitle: `品类全景对标（共${sorted.length}个品牌）· TOP10品牌跨平台提及分布`,
      tableLabel: `竞品 GEO 全景对标一览（TOP9 + 监测主体）`,
      tableHeaders: ['排名', '品牌 / 车型', '竞品类型', 'BCR', 'MRR', 'ES', 'CWR', 'SoV', 'GEO综合', '评级'],
      rows,
      tableNoteHtml: `注：① 表内竞品各维得分与${brand}同轮、同词库实测；CWR列标注「-」系 brand_stats 无逐竞品CWR字段（仅本品单独统计）；② 完整${sorted.length}品牌明细见原始数据留存。`,
      insightHtml,
      chartBrands,
      chartSeries
    };
  });
  // ── CH4.2 各平台排名热力图（platform_rankings 反 pivot） ──
  const platformHeatmapProps = computed(() => {
    const d = reportData.value;
    const names = platformNames.value;
    const rankings = d?.platform_rankings;
    const brands = brandsSorted.value;
    if (!Array.isArray(rankings) || !rankings.length || !names.length || !brands.length) return undefined;
    const heatBrands = [...brands.filter(b => !b.is_target).slice(0, 5), targetBrandRow.value].filter(Boolean);
    const rankByPlatformAndBrand = {};
    rankings.forEach(pr => {
      const m = {};
      (pr.ranking ?? []).forEach(r => m[r.name] = r.rank);
      rankByPlatformAndBrand[canonPlatform(pr.platform)] = m;
    });
    // 前3名用色阶徽标(span+背景色)，第4名及以后用纯文本，未进入榜单用红色徽标——与参考渲染的热力图例一致
    const SPAN_BASE = 'padding:3px 8px;border-radius:4px;font-size:11px;font-weight:600;';
    const RANK_SPAN = {
      1: `background:#EAF3DE;color:#27500A;${SPAN_BASE}`,
      2: `background:#E6F1FB;color:#0C447C;${SPAN_BASE}`,
      3: `background:#FAEEDA;color:#633806;${SPAN_BASE}`
    };
    const MISSING_SPAN = `background:#FCEBEB;color:#A32D2D;${SPAN_BASE}`;
    const rows = heatBrands.map(b => {
      const ranks = [];
      const cells = names.map(pf => {
        const r = rankByPlatformAndBrand[pf]?.[b.name];
        if (r == null) return {
          label: '-',
          spanStyle: MISSING_SPAN
        };
        ranks.push(r);
        if (r <= 3) return {
          label: CIRCLED[r - 1] ?? String(r),
          spanStyle: RANK_SPAN[r]
        };
        return {
          label: String(r),
          plain: true,
          cellStyle: 'font-size:11px;color:var(--geo-td);'
        };
      });
      const avg = ranks.length ? (ranks.reduce((a, b2) => a + b2, 0) / ranks.length).toFixed(1) : '未进入';
      return {
        name: b.is_target ? `${b.name} ★` : b.name,
        nameColor: b.is_target ? 'var(--geo-gold)' : undefined,
        cells,
        avg,
        avgStyle: ranks.length ? `font-weight:600;color:${rankColor(Number(avg))};` : 'color:var(--geo-td);',
        rowStyle: b.is_target ? 'background:rgba(255,77,79,.05);' : undefined
      };
    });
    const brand = clean(d.meta?.target_brand) ?? '本品';
    return {
      subTitle: `品牌 × ${names.length}平台AI推荐排名矩阵 · 识别${brand}在哪个平台被哪些竞品超越`,
      headers: ['品牌', ...names, '平均排名'],
      rows,
      noteHtml: `「-」表示该品牌未出现在该平台推荐名单中。绿色①第一、蓝色②第二、橙色③第三。`
    };
  });
  // ── CH4.3 PK 胜负记录 ──
  const pkRecordsProps = computed(() => {
    const d = reportData.value;
    const list = d?.pk_analysis;
    const names = platformNames.value;
    if (!Array.isArray(list) || !list.length) return undefined;
    const target = clean(d?.meta?.target_brand) ?? '目标';
    const pkInsightList = d.llm_generated?.pk_insight;
    const rows = list.map(pk => {
      // 旧契约 by_platform 是 {platform:value} 对象，非数组时按空数组处理，不再假设可 .find
      const byPlatform = Array.isArray(pk.by_platform) ? pk.by_platform : [];
      const evidence = clean(pk.ai_evidence_con) ?? clean(pk.ai_evidence_pro) ?? '本轮未采集到对比证据';
      return {
        versus: `${target} vs ${pk.competitor}`,
        tag: '竞品',
        tagStyle: 'color:var(--geo-td);',
        overall: `${pk.overall_win_rate ?? 0}%`,
        overallColor: KIND_COLOR.red,
        overallDetail: `胜${pk.win ?? 0} 负${pk.lose ?? 0} 平${pk.tie ?? 0}`,
        cells: names.map(pf => {
          const bp = byPlatform.find(x => canonPlatform(x.platform) === pf);
          return bp ? {
            value: `${bp.win_rate ?? 0}%`,
            color: KIND_COLOR.red
          } : {
            value: '-',
            color: KIND_COLOR.gray
          };
        }),
        losePoint: evidence,
        losePointStyle: 'font-size:12px;color:var(--geo-tm);'
      };
    });
    // pk_insight 为空数组时不渲染底部警示提示块（传空字符串而非 undefined，避免触发组件自带的 demo 默认文案）
    const insightHtml = (Array.isArray(pkInsightList) && pkInsightList.length ? clean(pkInsightList.join('；')) : '') ?? '';
    return {
      title: `${target} vs 竞品 PK 胜负记录`,
      subTitle: `直接对比场景中AI对${target}的立场 · 各竞品胜率 · 跨平台差异`,
      headers: ['对比对象', '整体胜率', ...names, '证据摘要'],
      rows,
      insightHtml
    };
  });
  // ── CH4.4 AI 叙事词竞争占位（dimension_matrix 三分类算法，N10 规则）──
  const narrativePositioningProps = computed(() => {
    const d = reportData.value;
    const dm = d?.dimension_matrix;
    if (!dm || !Array.isArray(dm.dimension_leaders) || !Array.isArray(dm.brands)) return undefined;
    const brand = clean(d.meta?.target_brand) ?? '';
    // 品牌全称与去掉常见主体后缀的简称，两者在 dimension_matrix.brands[]/dimension_leaders[] 里
    // 可能被记成两条不同条目（如"小米汽车"与"小米"），需都当作"本品"，否则会把本品自己的领先维度
    // 误判成"被竞品占领"（对应参考脚本 tgt_names=(BRAND, BRAND去掉"汽车"等后缀) 的口径）
    const shortBrand = brand.replace(/(汽车|集团|科技|品牌|公司)$/, '');
    const tgtNames = new Set([brand, shortBrand].filter(Boolean));
    const tgtDm = dm.brands.find(b => tgtNames.has(b.name)) ?? dm.brands.find(b => (b.name ?? '').includes(brand)) ?? dm.brands[0];
    const npLlm = d.llm_generated?.narrative_position ?? {};
    const occ = [];
    const winD = [];
    const buildD = [];
    const hardD = [];
    dm.dimension_leaders.forEach(x => {
      const cov = tgtDm?.coverage_pct?.[x.dimension] ?? 0;
      if (tgtNames.has(x.leader_brand)) {
        winD.push(`${x.dimension}（本品领先·${x.score}）`);
      } else {
        occ.push([x.dimension, x.leader_brand, x.score]);
        (cov >= 8 ? buildD : hardD).push(`${x.dimension}（${x.leader_brand} ${x.score} / 本品覆盖${round1(cov)}%）`);
      }
    });
    occ.sort((a, b) => b[2] - a[2]);
    const occupied = occ.slice(0, 6).map(([dimName, leader, score]) => ({
      dim: dimName,
      label: '主要占位：',
      ownersHtml: `<span style="color:var(--geo-red);">${leader}</span>（覆盖领先分 ${score}）`
    }));
    const groups = [{
      tag: '差异化窗口',
      color: 'var(--geo-green)',
      list: winD,
      fallback: '本品为该维度领先者，竞品尚未强占位，可扩展为独占叙事标签'
    }, {
      tag: '待建立',
      color: 'var(--geo-orange)',
      list: buildD,
      fallback: '本品已有覆盖但落后于领先者，差异化叙事可建立'
    }, {
      tag: '高竞争',
      color: 'var(--geo-red)',
      list: hardD,
      fallback: '本品覆盖近零且已被竞品深度占位，需绕行或长线投入'
    }];
    // desc 用真实 <br> 换行分隔"内容行"与"规则说明小字行"，与参考渲染的两行式排版一致（而非用全角空格挤在一行）
    const targetPositions = groups.map(g => ({
      tag: g.tag,
      tagColor: g.color,
      desc: `${g.list.join('、') || '—'}<br><span style="font-size:11px;color:var(--geo-td);">${clean(npLlm[g.tag]) ?? g.fallback}</span>`
    }));
    const posn = (tgtDm?.top_positive_narratives ?? []).slice(0, 5);
    const negn = (tgtDm?.top_negative_narratives ?? []).slice(0, 5);
    return {
      subTitle: `各竞品在核心叙事维度的覆盖率分布 · ${brand}当前叙事词位置`,
      targetTitle: `${brand} 当前叙事词位置`,
      occupied,
      targetPositions,
      bubbleData: [],
      narrativeWordsTitle: `${brand} 叙事词（dimension_matrix）`,
      narrativeWordsHtml: `<strong style="color:var(--geo-green);">正面：</strong>${posn.join('、') || '—'}<br><strong style="color:var(--geo-red);">负面：</strong>${negn.join('、') || '—'}`
    };
  });
  // ── CH4.5 宣传维度对比（热力矩阵）──
  const dimensionComparisonProps = computed(() => {
    const dm = reportData.value?.dimension_matrix;
    if (!dm || !Array.isArray(dm.dimensions) || !Array.isArray(dm.brands)) return undefined;
    const dimensions = dm.dimensions;
    // 按各品牌12维度覆盖率总和降序取TOP7，而非按brands[]原始顺序硬切——
    // dimension_matrix.brands[] 本身不是按相关性/得分排的，原始顺序前7名大多是覆盖率全0的长尾品牌，
    // 热力图会显得"数据严重缺失"，改成按总覆盖率排序才能让真正有叙事数据的品牌（含本品）显示出来
    const brandsTop7 = [...dm.brands].sort((a, b) => {
      const sumA = Object.values(a.coverage_pct ?? {}).reduce((s, v) => s + (Number(v) || 0), 0);
      const sumB = Object.values(b.coverage_pct ?? {}).reduce((s, v) => s + (Number(v) || 0), 0);
      return sumB - sumA;
    }).slice(0, 7);
    const brands = brandsTop7.map(b => b.name);
    // ECharts heatmap series 的数据点格式是 [x轴值, y轴值, value]；这里 xAxis=品牌、yAxis=维度，
    // 顺序必须是 [品牌, 维度, 覆盖率]，颠倒会导致坐标系里找不到匹配类目、格子完全不渲染
    const heatmapData = [];
    brandsTop7.forEach(b => {
      dimensions.forEach(dim => {
        const v = b.coverage_pct?.[dim];
        heatmapData.push([b.name, dim, v === undefined || v === null ? null : v]);
      });
    });
    return {
      heatmapDimensions: dimensions,
      heatmapBrands: brands,
      heatmapData
    };
  });
  // ── CH5.3 维度 × 品牌覆盖率矩阵（口径D6①：新schema无关键词×品牌命中矩阵，复用 dimension_matrix 数据，
  //    挂载时改用 4.5 同款热力图组件承接，故与 4.5 同源数据、独立文案）──
  const dimensionMatrixTableProps = computed(() => {
    const base = dimensionComparisonProps.value;
    if (!base) return undefined;
    return {
      ...base,
      sectionNo: '5.3',
      title: '维度 × 品牌覆盖率矩阵',
      subTitle: '口径D6①：以维度×品牌覆盖率矩阵呈现（原关键词×品牌命中矩阵无数据支撑）',
      chartTitle: '热力矩阵：TOP7 品牌 × 核心传播维度',
      chartNote: '单元格为该品牌在该传播维度的语料覆盖率% · 数据源 dimension_matrix，与4.5同源'
    };
  });
  // ── CH4.6 头部竞品 AI 占位画像 ──
  const competitorProfilesProps = computed(() => {
    const d = reportData.value;
    if (!d) return undefined;
    const brand = clean(d?.meta?.target_brand) ?? '';
    const list = (d?.category_benchmark?.competitor_decoding ?? []).filter(c => c.name !== brand);
    if (!list.length) {
      return undefined;
    }
    const profiles = list.slice(0, 3).map(c => {
      const kind = kindOfScore(c.geo_score ?? 0);
      const keyPhrases = (c.key_phrases ?? []).map(x => typeof x === 'string' ? x : x.phrase).filter(Boolean);
      const quotes = c.ai_quotes ?? [];
      const quoteText = clean(quotes[0]);
      return {
        name: c.name,
        geoLabel: `GEO ${c.geo_score ?? 0}`,
        accent: geoScoreColor(c.geo_score ?? 0),
        pillKind: kind,
        threatLabel: (c.leading_dimensions ?? []).length ? `领先维度：${c.leading_dimensions.join(' / ')}` : '威胁类型：竞品占位',
        whyWinHtml: `<strong style="color:var(--geo-t);">关键短语：</strong>${keyPhrases.slice(0, 3).join('、') || '—'}`,
        suppressHtml: `<strong style="color:var(--geo-t);">胜因：</strong>${clean(c.why_win) ?? '—'}`,
        responseHtml: quoteText ? `${quoteText.slice(0, 110)}…` : ''
      };
    });
    return {
      profiles,
      noteHtml: '注：竞品语料逐源逐词深度拆解属于后续服务交付内容，可在运营启动后按竞品出具专项拆解。'
    };
  });
  // ── CH5.1 监测词分类体系（新schema无 keyword_taxonomy，改用 keyword_coverage.categories 类目覆盖统计）──
  const keywordClassificationProps = computed(() => {
    const d = reportData.value;
    const kc = d?.keyword_coverage;
    if (!kc || !Array.isArray(kc.categories) || !kc.categories.length) return undefined;
    const catColor = pct => pct >= 50 ? 'var(--geo-green)' : pct >= 30 ? 'var(--geo-orange)' : 'var(--geo-red)';
    const rows = kc.categories.map(c => ({
      category: `${c.category}（${CATEGORY_LABEL_MAP[c.category] ?? c.display_name ?? c.category}）`,
      total: String(c.total_keywords ?? 0),
      covered: String(c.covered_keywords ?? 0),
      pct: c.coverage_pct ?? 0,
      color: catColor(c.coverage_pct ?? 0)
    }));
    const meta = d.meta ?? {};
    const stages = d.funnel?.stages ?? [];
    const journeyAllZero = stages.length > 0 && stages.every(s => (s.coverage_rate_pct ?? 0) <= 0);
    const overallPct = kc.overall_coverage_pct ?? 0;
    const overallNoteHtml = `${meta.total_keywords ?? 0}条监测词的总体命中率。${journeyAllZero && overallPct > 0 ? '注意：与5.4旅程阶段覆盖率（全0）口径不同，两者矛盾已列入数据告警（见_backfill_report），以上游裁决为准。' : ''}`;
    return {
      subTitle: `${meta.total_keywords ?? 0}条监测词 · ${kc.categories.length}个类目 · 总体覆盖率 ${overallPct}%`,
      rows,
      overallPct,
      overallColor: 'var(--geo-orange)',
      overallNoteHtml
    };
  });
  // ── CH5.2 类目词覆盖分析（keyword_details 到位则大表，否则空态说明）──
  const categoryCoverageProps = computed(() => {
    const d = reportData.value;
    const kc = d?.keyword_coverage;
    if (!kc) return undefined;
    const kws = d.keyword_details ?? [];
    const STATUS_CLS = {
      命中: 'status-hit',
      部分命中: 'status-risk',
      缺席: 'status-miss'
    };
    const catDn = {};
    (kc.categories ?? []).forEach(c => catDn[c.category] = CATEGORY_LABEL_MAP[c.category] ?? c.display_name ?? c.category);
    const legend = {
      label: '状态图例',
      items: [{
        badgeClass: 'status-hit',
        badgeText: '命中',
        desc: '全部监测平台均命中本品'
      }, {
        badgeClass: 'status-risk',
        badgeText: '部分命中',
        desc: '仅部分监测平台命中本品'
      }, {
        badgeClass: 'status-miss',
        badgeText: '缺席',
        desc: '全部监测平台均未命中本品'
      }]
    };
    if (kws.length) {
      const rows = kws.map(k => {
        const hits = k.platform_hits ?? [];
        const isTargetHit = hit => {
          if (typeof hit.target_hit === 'boolean') return hit.target_hit;
          return ['命中', '命中需校准', '事实偏差', '混淆', '高风险命中', '偶发命中'].includes(hit.hit_status);
        };
        const hitCount = hits.filter(isTargetHit).length;
        const st = hitCount === 0 ? '缺席' : hitCount === hits.length ? '命中' : '部分命中';
        const hitPlatforms = Array.from(new Map(hits.filter(isTargetHit).map(h => {
          const name = canonPlatform(h.platform ?? h.platform_name ?? h.display_name ?? h.name);
          const icon = ALL_MODELS.find(m => platformKeyOf(m.label) === platformKeyOf(name))?.icon ?? '';
          return {
            name,
            icon
          };
        }).filter(p => p.name).map(p => [platformKeyOf(p.name), p])).values());
        const bhit = Array.from(new Set(hits.flatMap(h => h.brands_hit ?? [])));
        const note = clean(k.risk_note) ?? (bhit.length ? `${bhit.slice(0, 4).join('、')} 命中` : '—');
        return {
          kw: {
            cls: 'geo-kw-main',
            text: k.keyword ?? ''
          },
          status: {
            cls: STATUS_CLS[st] ?? 'status-miss',
            text: st
          },
          hitPlatforms,
          risk: note,
          catCell: {
            rowspan: 1,
            text: clean(k.intent_category) ?? catDn[k.category] ?? k.category ?? ''
          },
          coreCell: {
            rowspan: 1,
            text: clean(k.core_intent_word) ?? clean(k.intent_group) ?? '—',
            sub: ''
          }
        };
      }).sort((a, b) => {
        const catA = a.catCell.text;
        const catB = b.catCell.text;
        if (catA === catB) return 0;
        if (!catA) return 1;
        if (!catB) return -1;
        return catA.localeCompare(catB, 'zh-CN');
      });
      return {
        subTitle: '按"意图分类 → 核心意图词 → 意图提示词"组织 · 合并单元格展示同类问题簇 · 标注跨平台命中结果',
        legend,
        headers: ['意图分类', '核心意图词', '意图提示词', '状态', '命中平台', '竞品覆盖 / 风险说明'],
        rows,
        noteHtml: '注：「核心意图词」是具体提示词的上一级语义归类；状态按各监测平台对本品的命中情况汇总，命中平台以图标展示（数据源 keyword_details）。'
      };
    }
    // keyword_details 逐词数据未就绪：降级为类目级覆盖率卡片（每类目一张卡，大字号百分比+X/Y词命中）
    const categoryCards = (kc.categories ?? []).map(c => {
      const pct = c.coverage_pct ?? 0;
      return {
        category: `${c.category}（${CATEGORY_LABEL_MAP[c.category] ?? c.display_name ?? c.category}）`,
        pct,
        ratio: `${c.covered_keywords ?? 0}/${c.total_keywords ?? 0} 词命中`,
        color: pct >= 50 ? 'var(--geo-green)' : pct >= 30 ? 'var(--geo-orange)' : 'var(--geo-red)'
      };
    });
    return {
      subTitle: '按"意图分类 → 核心意图词 → 意图提示词"组织 · 合并单元格展示同类问题簇',
      legend,
      categoryCards,
      headers: ['意图分类', '核心意图词', '意图提示词', '状态', '命中平台', '竞品覆盖 / 风险说明'],
      rows: [],
      noteHtml: ''
    };
  });
  // ── CH5.4 消费旅程覆盖链路 ──
  const journeyCoverageChainProps = computed(() => {
    const stages = reportData.value?.funnel?.stages;
    if (!Array.isArray(stages) || !stages.length) return undefined;
    const minCov = Math.min(...stages.map(s => s.coverage_rate_pct ?? 0));
    let bpIdx = stages.findIndex(s => (s.coverage_rate_pct ?? 0) === minCov && s.geo_dimension === 'CWR');
    if (bpIdx < 0) bpIdx = stages.findIndex(s => (s.coverage_rate_pct ?? 0) === minCov);
    const nodes = [];
    stages.forEach((s, i) => {
      const pct = s.coverage_rate_pct ?? 0;
      const color = coverageColor(pct);
      const query = String(s.query ?? '').replace(/[“”"]/g, '');
      const bp = i === bpIdx;
      const threat = clean(s.threat) ?? '';
      const threatShown = threat.length > 44 ? `${threat.slice(0, 44)}…` : threat;
      nodes.push({
        stage: {
          broken: bp,
          badge: bp ? '✕ AI链路断点' : undefined,
          iconStyle: `color:${color};`,
          name: s.stage,
          dim: [s.geo_dimension, s.priority].filter(Boolean).join(' · '),
          pct: `${pct}%`,
          pctColor: color,
          cnt: `${s.hit_keywords ?? 0}/${s.total_keywords ?? 0} 词`,
          fillWidth: `${Math.min(pct, 100)}%`,
          fillColor: color,
          kwHtml: `${query ? `代表词「${query}」` : ''}${threatShown ? `<br><span style="color:${color};">${threatShown}</span>` : ''}`
        }
      });
      if (i < stages.length - 1) nodes.push({
        link: {
          symbol: i === bpIdx ? '✕' : '→',
          broken: i === bpIdx
        }
      });
    });
    const d = reportData.value;
    const funnel = d.funnel ?? {};
    const allZero = stages.every(s => (s.coverage_rate_pct ?? 0) <= 0);
    const conclusionHtml = `<strong>链路结论：</strong>${clean(d.llm_generated?.funnel_insight) ?? (allZero ? `全部${stages.length}个消费旅程阶段覆盖率均为0，属全链路缺席。` : '') + (clean(funnel.description) ?? '')}`;
    const breakthroughHtml = clean(funnel.breakthrough_text) ? `<strong>破局点：</strong>${funnel.breakthrough_text}` : '';
    return {
      subTitle: `${stages.length}阶段独立覆盖率 · 阶段对应GEO维度 · 断点标记`,
      chainTitle: `AI 端消费旅程 · ${stages.length} 阶段覆盖链路`,
      nodes,
      noteHtml: '注：各阶段为<strong>独立覆盖率</strong>（非逐层转化率）；断点＝覆盖率最低阶段（同为最低时优先 CWR 维度）；逐词×逐平台命中明细依赖 keyword_details（缺口①②）。',
      conclusionHtml,
      breakthroughHtml
    };
  });
  // ── CH5.5 缺席高价值关键词 ──
  // intent_strength（意图强度）/ absent_keywords_brands（竞品品牌）为后端新增字段，尚无真实样例返回，
  // 按约定用法防御式读取：优先取 keyword_details 匹配项上的同名字段，其次取 funnel.stages 里与 top3_missing 同下标平行的数组，
  // 取不到时分别保持空白 / 退回旧的 brands_hit 反查逻辑——后端返回结构如与此假设不符，需要再核对调整。
  function brandsText(v) {
    if (Array.isArray(v)) return v.filter(Boolean).slice(0, 3).join('·');
    if (typeof v === 'string') return clean(v) ?? '';
    return '';
  }
  function strengthText(v) {
    if (v == null) return '';
    return clean(String(v)) ?? '';
  }
  const missingKeywordsProps = computed(() => {
    const d = reportData.value;
    const stages = d?.funnel?.stages ?? [];
    const kws = d?.keyword_details ?? [];
    const rows = [];
    stages.forEach(s => {
      ;
      (s.top3_missing ?? []).forEach((m, idx) => {
        const matched = kws.find(k => k.keyword === m);
        const bhit = matched ? Array.from(new Set((matched.platform_hits ?? []).flatMap(h => h.brands_hit ?? []))) : [];
        const competitors = matched && brandsText(matched.absent_keywords_brands) || brandsText(s.absent_keywords_brands?.[idx]) || bhit.slice(0, 3).join('·');
        const intentLabel = matched && strengthText(matched.intent_strength) || strengthText(s.intent_strength?.[idx]);
        rows.push({
          keyword: m,
          intentLabel,
          intentPillKind: 'geo-pill-orange',
          competitors,
          stage: s.stage,
          impact: clean(s.threat) ?? '',
          impactColor: KIND_COLOR.red
        });
      });
    });
    const usedFallback = !rows.length;
    if (usedFallback) {
      kws.forEach(k => {
        const hits = k.platform_hits ?? [];
        const targetMissed = hits.length && !hits.some(h => h.target_hit);
        if (targetMissed) {
          const bhit = Array.from(new Set(hits.flatMap(h => h.brands_hit ?? [])));
          if (bhit.length) {
            rows.push({
              keyword: k.keyword,
              intentLabel: strengthText(k.intent_strength),
              intentPillKind: 'geo-pill-orange',
              competitors: brandsText(k.absent_keywords_brands) || bhit.slice(0, 3).join('·'),
              stage: k.funnel_stage ?? '',
              impact: clean(k.risk_note) ?? '竞品命中而本品缺席',
              impactColor: KIND_COLOR.orange
            });
          }
        }
      });
    }
    if (!d) return undefined;
    const brand = clean(d.meta?.target_brand) ?? '本品';
    const sourceLabel = usedFallback ? '逐词命中明细实测结果' : '消费旅程阶段实测缺口（竞品覆盖品牌取自逐词命中明细）';
    return {
      title: `${brand} AI 缺席高价值关键词清单`,
      subTitle: `${brand}未出现但竞品命中的关键词 · ${sourceLabel}`,
      rows
    };
  });
  // ── CH6.0 AI内容风险诊断总览（固定6类模板+实测值参数化，risk_overview 本身不驱动内容）──
  const riskOverviewProps = computed(() => {
    const d = reportData.value;
    if (!d) return undefined;
    const t = d.target_overview ?? {};
    const fr = d.factual_risk ?? {};
    const bns = d.bns_analysis ?? {};
    const kc = d.keyword_coverage ?? {};
    const sd = fr.severity_distribution ?? {};
    const rank = targetBrandRow.value?.rank_by_geo ?? (d.brand_stats ?? []).length;
    const top1 = categoryTop1.value;
    const geo = t.GEO ?? 0;
    const gap = top1 ? Math.max(0, round1((top1.GEO ?? 0) - geo)) : 0;
    const negLen = (t.negative_phrases ?? []).length;
    // 与 6.6 相同口径：按 flawed_types 编码计数、按 flawed_status 计数（过期信息/底表无对应/疑似失真）
    const cd = fr.claim_details ?? [];
    const scount = {};
    const tcount = {};
    cd.forEach(c => {
      scount[c.flawed_status ?? ''] = (scount[c.flawed_status ?? ''] ?? 0) + 1;
      (c.flawed_types ?? []).forEach(x => {
        const code = x.split(' ')[0];
        tcount[code] = (tcount[code] ?? 0) + 1;
      });
    });
    const staleN = tcount['FRR-03'] ?? 0;
    const missingN = scount['底表无对应项'] ?? 0;
    const suspectN = scount['疑似失真'] ?? 0;
    const riskCategories = d.narrative?.risk_categories ?? [];
    const cards = [{
      title: 'AI可见度不足',
      desc: '品类泛搜索和推荐型问题中，进入稳定候选集程度由 BCR/MRR 决定，用户在早期认知阶段的可接触度见右侧指标。',
      tags: [`BCR ${t.BCR ?? 0}`, `MRR ${t.MRR ?? 0}`, `词覆盖 ${kc.overall_coverage_pct ?? 0}%`]
    }, {
      title: '内容时效缺口',
      desc: '过期信息与底表空白代表 AI 尚未及时吸收官方口径，是幻觉高发带。',
      tags: [`过期信息 ${staleN}条`, `底表无对应 ${missingN}条`, `疑似失真 ${suspectN}条`]
    }, {
      title: '推荐优先级不足',
      desc: '即使被提及，是否形成优先推荐位由品类排名与声量份额体现。',
      tags: [`品类第${rank}`, `SoV ${t.SoV ?? 0}`, `MRR ${t.MRR ?? 0}`]
    }, {
      title: '事实口径偏差',
      desc: '数值、归属、规格类断言偏差直接拉低信息可信度。',
      tags: [`IA ${t.IA ?? 0}`, `FRR ${fr.frr_rate_pct ?? 0}%`, `高严重度 ${sd['高'] ?? 0}条`]
    }, {
      title: '竞品叙事压制',
      desc: '对比和选购问题中 AI 的立场分布；本轮对战明细缺失，胜负不可解读。',
      tags: [cwrDetailMissing.value ? 'CWR明细缺失' : `CWR ${t.CWR ?? 0}`, ...(top1 ? top1.is_target ? ['当前品类第一'] : [`品类第一 ${top1.name}`, `差距 ${gap}分`] : [])]
    }, {
      title: '负面声誉覆盖',
      desc: '负面叙事的跨平台覆盖度，削弱转化信任的程度。',
      tags: [`SS ${t.SS ?? 0}`, `负面率 ${bns.avg_negative_rate ?? 0}%`, `负面词 ${negLen}条`, ...riskCategories.slice(0, 3).map(r => `${r.category} ${r.score}分`)]
    }];
    const regItemsLen = d.regulatory_compliance?.brand_risk_item_count ?? (d.regulatory_compliance?.brand_risk_items ?? []).length;
    const avgNeg = bns.avg_negative_rate ?? 0;
    const RISK_ALERT = 15; // 与 6.1 品牌声誉监测(reputationMonitoringProps) ALERT 阈值保持一致
    const statRow = [{
      value: `${avgNeg}%`,
      tone: avgNeg >= RISK_ALERT ? 'red' : '',
      labelHtml: `平均负面率<br>${avgNeg >= RISK_ALERT ? '高于行业警戒线' : '全平台安全'}`
    }, {
      value: `${fr.frr_rate_pct ?? 0}%`,
      tone: 'red',
      labelHtml: `FRR 事实缺陷率<br>${fr.flawed_claims ?? 0}/${fr.total_claims ?? 0}条断言`
    }, {
      value: `${sd['高'] ?? 0}`,
      tone: 'red',
      labelHtml: `高严重度缺陷<br>中${sd['中'] ?? 0} · 低${sd['低'] ?? 0}`
    }, {
      value: `${regItemsLen}`,
      tone: '',
      labelHtml: `监管风险表述<br>行业：${fr.regulatory_industry ?? ''}`
    }];
    return {
      cards,
      statRow
    };
  });
  // ── CH6.1 品牌声誉监测 ──
  const reputationMonitoringProps = computed(() => {
    const d = reportData.value;
    const bns = d?.bns_analysis;
    if (!bns) return undefined;
    const ALERT = 15;
    const pct = v => `${round1(v ?? 0)}%`;
    const rateColor = v => (v ?? 0) >= ALERT ? KIND_COLOR.red : (v ?? 0) >= 8 ? KIND_COLOR.orange : KIND_COLOR.green;
    const avgNeg = bns.avg_negative_rate ?? 0;
    const platformBars = (bns.by_platform ?? []).length ? {
      title: '各平台负面提及率监测',
      bars: bns.by_platform.map(p => ({
        label: canonPlatform(p.platform),
        value: pct(p.negative_rate),
        color: rateColor(p.negative_rate)
      })),
      summaryHtml: `${platformNames.value.length}平台平均负面率：<strong style="color:${rateColor(avgNeg)}">${round1(avgNeg)}%</strong>（行业警戒线 ${ALERT}%）`
    } : undefined;
    const compBns = (bns.competitor_bns ?? []).filter(x => x.name);
    const brand = clean(d.meta?.target_brand) ?? '本品';
    const benchmarkBars = compBns.length ? {
      title: '竞品母品牌负面率基准对比',
      bars: [{
        label: `${brand} ⭐`,
        value: pct(avgNeg),
        color: KIND_COLOR.red
      }, ...compBns.map(c => ({
        label: c.name,
        value: pct(c.negative_rate),
        color: KIND_COLOR.blue
      }))],
      diagnosisHtml: (() => {
        const peerAvg = compBns.reduce((s, c) => s + (c.negative_rate ?? 0), 0) / compBns.length;
        return `${brand}负面率 ${round1(avgNeg)}%，竞品母品牌均值 ${round1(peerAvg)}%，${avgNeg <= peerAvg ? '低于' : '高于'}竞品均值。`;
      })()
    } : undefined;
    const negFreq = {};
    (d.target_overview?.negative_phrases ?? []).forEach(p => negFreq[p.phrase] = p.freq);
    const ranked = bns.all_negative_phrases_ranked ?? [];
    const negativeWords = {
      title: '高频负面叙事词（跨平台合并，按出现频次降序）',
      words: ranked.length ? ranked.map(ph => ({
        word: ph,
        meta: `出现${negFreq[ph] ?? 1}次`,
        bg: 'var(--geo-red-d)',
        color: 'var(--geo-red)'
      })) : [{
        word: '本轮未检出负面叙事词',
        meta: '',
        bg: 'rgba(17,24,39,.03)',
        color: 'var(--geo-td)'
      }]
    };
    const dimExplain = {
      title: '本模块指标维度说明',
      items: [{
        label: '归属指标',
        html: `归属 <strong>SS 情感倾向分</strong>的负向子指标，用于识别AI回答中的负向叙事压力，同时作为 IA 风险辅助观察项。`
      }, {
        label: '计算口径',
        html: '负面提及率 ＝ 含负向叙事的有效回答数 ÷ 该平台有效回答数。'
      }, {
        label: '判读阈值',
        html: `行业警戒线 <strong>${ALERT}%</strong>；本轮${platformNames.value.length}平台均值 <strong style="color:${rateColor(avgNeg)}">${round1(avgNeg)}%</strong>，${avgNeg >= ALERT ? '高于' : '低于'}警戒线。`
      }],
      footerHtml: `声誉监测每轮跟踪，负面提及率${avgNeg >= ALERT ? '已超过' : '尚低于'}行业警戒线，${avgNeg >= ALERT ? '建议本月内响应处置' : '持续观察即可'}。`
    };
    const diagnosisHtml = clean(d.llm_generated?.bns_diagnosis) ?? `本轮平均负面率为 ${round1(avgNeg)}%，暂无竞品基准可比。`;
    return {
      platformBars,
      benchmarkBars,
      negativeWords,
      dimExplain,
      diagnosisHtml
    };
  });
  // ── CH6.2 AI 叙事词分析 ──
  const narrativeWordAnalysisProps = computed(() => {
    const d = reportData.value;
    const t = d?.target_overview;
    if (!t) return undefined;
    const posPhrases = t.positive_phrases ?? [];
    const negPhrases = t.negative_phrases ?? [];
    const pos = posPhrases.map(p => p.phrase).filter(Boolean);
    const neg = negPhrases.map(p => p.phrase).filter(Boolean);
    const correctWords = (d.narrative?.correct_words ?? []).filter(Boolean);
    const riskWords = (d.narrative?.risk_words ?? []).filter(Boolean);
    const freqMap = {};
    [...posPhrases, ...negPhrases].forEach(p => freqMap[p.phrase] = p.freq ?? 1);
    const toWords = arr => Array.from(new Set(arr)).map(text => ({
      text,
      freq: freqMap[text] ?? 1
    }));
    const classify = {
      title: `${clean(d.meta?.target_brand) ?? '本品'} 叙事词分类（跨平台合并）`,
      groups: [{
        label: `正面叙事词（${pos.length}）`,
        color: 'var(--geo-green)',
        tagBg: 'rgba(22,163,74,.06)',
        borderColor: 'rgba(22,163,74,.25)',
        words: toWords([...pos, ...correctWords])
      }, {
        label: `负面叙事词（需反制·${neg.length}）`,
        color: 'var(--geo-red)',
        tagBg: 'rgba(220,38,38,.05)',
        borderColor: 'rgba(220,38,38,.25)',
        words: toWords([...neg, ...riskWords])
      }]
    };
    const ppm = d.phrase_platform_matrix ?? [];
    const names = platformNames.value;
    const showConsistency = names.length > 1;
    const consistency = ppm.length ? {
      title: '跨平台叙事词一致性',
      platforms: names,
      rows: ppm.map(x => {
        const byP = {};
        (x.by_platform ?? []).forEach(b => byP[canonPlatform(b.platform)] = b.freq);
        const nHit = names.filter(p => (byP[p] ?? 0) > 0).length;
        const cons = nHit === names.length ? '全平台一致' : nHit > 1 ? '部分' : nHit === 1 ? '独有' : '未出现';
        return {
          word: x.phrase,
          cells: names.map(p => (byP[p] ?? 0) > 0 ? {
            symbol: '✓',
            color: 'var(--geo-green)'
          } : {
            symbol: '-',
            color: 'var(--geo-td)'
          }),
          badge: {
            text: cons,
            bg: 'rgba(17,24,39,.06)',
            color: 'var(--geo-tm)'
          }
        };
      }),
      legend: '✓出现 / -未出现'
    } : {
      title: '跨平台叙事词一致性',
      platforms: names,
      rows: [],
      legend: `叙事词×平台出现矩阵（phrase_platform_matrix）本轮无数据；当前叙事短语合计 ${pos.length + neg.length} 条（正面${pos.length} / 负面${neg.length}）。`
    };
    // 只监测单一平台时，"跨平台一致性"不成立（无从比较），隐藏该卡片，左侧分类卡占满整行
    return {
      classify,
      consistency: showConsistency ? consistency : null
    };
  });
  // ── CH6.3 原话摘录 ──
  const aiQuotesProps = computed(() => {
    const d = reportData.value;
    const quotes = d?.target_overview?.ai_quotes;
    if (!Array.isArray(quotes) || !quotes.length) return undefined;
    const byPlatform = {};
    quotes.forEach(q => {
      const {
        platform,
        confidence,
        text
      } = quoteMeta(q);
      const t = clean(text);
      if (!t) return;
      const pfName = canonPlatform(platform) || 'AI平台';
      (byPlatform[pfName] ??= []).push({
        text: t,
        srcHtml: `- ${pfName} 实测原话${confidence ? `（置信${confidence}）` : ''}`
      });
    });
    const colors = ['#4F8EF7', '#1677FF', '#0FC6C2', '#F59E0B'];
    const columns = Object.keys(byPlatform).map((pf, i) => ({
      headerLabel: pf,
      headerColor: colors[i % colors.length],
      quotes: byPlatform[pf]
    }));
    const meta = d.meta ?? {};
    return columns.length ? {
      columns,
      footerHtml: `注：本节为代表性证据卡摘录；全部${meta.total_answers ?? 0}次AI响应原文（含平台、触发词与时间戳）已全量归档留存（获取品牌${meta.total_records ?? 0}个），可按需调阅复核。`
    } : undefined;
  });
  // ── CH6.4 引用源 ──
  const citationAnalysisProps = computed(() => {
    const d = reportData.value;
    if (!d) return undefined;
    // 后端 layer 字段历史存在英文/中文两种取值，统一归一化为规范 key
    const LAYER_NORMALIZE = {
      official: 'official',
      vertical: 'vertical',
      vertical_auto: 'vertical',
      thirdparty: 'thirdparty',
      thirdparty_review: 'thirdparty',
      social: 'social',
      risk_repost: 'risk',
      官方数据: 'official',
      官方型: 'official',
      奖项型: 'official',
      媒体评测: 'vertical',
      销量数据: 'vertical',
      用户口碑: 'social',
      专业机构: 'thirdparty',
      行业报告: 'thirdparty',
      数据型: 'thirdparty',
      来源型: 'thirdparty'
    };
    function norLayer(raw) {
      return LAYER_NORMALIZE[raw] ?? raw;
    }
    // 按平台拆分：citation_source.results.<平台> 各自包含 sourceTop15 / sourceLayering / gapDiagnosis / buildDirections
    const csResults = d.citation_source?.results ?? {};
    const pfNames = Object.keys(csResults);
    if (!pfNames.length) return undefined;
    const platforms = pfNames.map(pfName => {
      const pf = csResults[pfName];
      // sourceTop15 → 引用源条形图数据
      const srcRanking = (pf?.sourceTop15?.items ?? []).map((s, i) => ({
        rank: i + 1,
        name: s.source ?? '',
        layer: norLayer(s.layer),
        freq: s.count ?? 0,
        share: s.share
      }));
      // sourceLayering → 引用源类型分层
      const sl = pf?.sourceLayering;
      const layers = sl && Array.isArray(sl.cards) && sl.cards.length ? {
        title: clean(sl.title) ?? '引用源类型分层',
        note: clean(sl.subtitle) ?? '',
        items: sl.cards.map(c => ({
          key: c.key ?? '',
          name: c.name ?? c.key ?? '',
          desc: clean(c.desc) ?? '',
          tag: `优先级 ${c.priority ?? ''}`,
          priority: c.priority ?? '',
          color: c.color ?? '#7F8C8D',
          count: c.count ?? 0,
          share: c.share ?? 0,
          topMembers: Array.isArray(c.topMembers) ? c.topMembers : []
        }))
      } : undefined;
      // gapDiagnosis → 引用源缺口诊断
      const gd = pf?.gapDiagnosis;
      const gap = gd && Array.isArray(gd.cards) && gd.cards.length ? {
        title: clean(gd.title) ?? '引用源缺口诊断',
        note: clean(gd.subtitle) ?? '',
        items: gd.cards.map(c => ({
          title: c.title ?? '',
          body: [c.metric ? `<strong style="color:var(--geo-red);">${c.metric}</strong>` : '', c.detail ?? ''].filter(Boolean).join('<br>'),
          high: c.severity === 'high'
        }))
      } : undefined;
      // buildDirections → 引用源建设方向
      const bd = pf?.buildDirections;
      const directions = bd && Array.isArray(bd.items) && bd.items.length ? {
        title: clean(bd.title) ?? '引用源建设方向（方向级）',
        note: clean(bd.subtitle) ?? '',
        items: bd.items.map(x => x.name ?? '').filter(Boolean)
      } : undefined;
      return {
        name: pfName,
        sourceRanking: srcRanking,
        layers,
        gap,
        directions
      };
    });
    return {
      platforms
    };
  });
  // ── CH6.5 信息准确性核查（factual_risk.top_risk_fields + claim_details 聚合）──
  const accuracyVerificationProps = computed(() => {
    const d = reportData.value;
    const fr = d?.factual_risk;
    if (!fr || !Array.isArray(fr.top_risk_fields) || !fr.top_risk_fields.length) return undefined;
    const claimByField = {};
    (fr.claim_details ?? []).forEach(c => {
      if (!claimByField[c.field_name]) claimByField[c.field_name] = c;
    });
    const rows = fr.top_risk_fields.map(x => {
      const claim = claimByField[x.field_name] ?? {};
      return {
        dim: x.field_name,
        correct: clean(claim.correct_value) ?? '—',
        actual: {
          text: x.raw_text ?? ''
        },
        platforms: clean(claim.platform) ? canonPlatform(claim.platform) : '—',
        statusPill: {
          label: x.severity ?? '',
          kind: x.severity === '高' ? 'red' : x.severity === '中' ? 'orange' : 'green'
        }
      };
    });
    return {
      subTitle: `事实断言 ${fr.total_claims ?? 0} 条 · 缺陷 ${fr.flawed_claims ?? 0} 条（疑似另 ${fr.suspected_flawed_claims ?? 0} 条）· FRR ${fr.frr_rate_pct ?? 0}%`,
      headers: ['风险字段', '严重度', 'AI实际表述', '主要偏差平台', '正确口径'],
      rows,
      tableNoteHtml: ''
    };
  });
  // ── CH6.6 平台幻觉与系统性风险记录（source_gap_cards + 监管表述 + 合规核查）──
  const hallucinationRecordsProps = computed(() => {
    const d = reportData.value;
    if (!d) return undefined;
    const gapCards = d?.llm_generated?.source_gap_cards ?? [];
    const rc = d.regulatory_compliance ?? {};
    const regItems = rc.brand_risk_items ?? [];
    const regItemsCount = rc.brand_risk_item_count ?? regItems.length;
    if (!gapCards.length && !regItems.length && !rc.rcr_compliance_status) return undefined;
    const records = gapCards.map((card, i) => {
      const sevKind = card.severity === 'high' ? 'red' : card.severity === 'medium' ? 'orange' : 'green';
      const platforms = (card.platforms ?? []).filter(Boolean);
      return {
        num: String(i + 1),
        cls: '',
        title: card.title ?? '',
        status: card.metric ?? '',
        bodyHtml: clean(card.detail) ?? '',
        platforms
      };
    });
    const regRows = regItems.slice(0, 5).map(x => ({
      text: x.text,
      riskType: x.risk_type,
      severity: x.severity
    }));
    const riskOverviewLen = (d.risk_overview ?? []).length;
    return {
      subTitle: '三类风险分级记录· 监管风险表述 · 合规核查',
      records,
      regulatoryTable: {
        title: `监管风险表述（TOP5 / 共${regItemsCount}条）`,
        rows: regRows
      },
      complianceCard: {
        title: '合规核查结论',
        html: `RCR 合规核查：<strong>${rc.rcr_compliance_status ?? ''}</strong><br>活跃违规：${rc.has_active_violation ? '有' : '无'}${(rc.compliance_items ?? []).length ? `<br>核查项：${(rc.compliance_items ?? []).join('、')}` : ''}<br>风险总览本轮${riskOverviewLen ? `共${riskOverviewLen}条` : '为空'}，风险判断以7.1分级诊断清单为准。`
      }
    };
  });
  // ── CH7.1 分级问题清单 ──
  const problemTriageProps = computed(() => {
    const d = reportData.value;
    const list = d?.problem_diagnosis;
    if (!Array.isArray(list) || !list.length) return undefined;
    const diagNarratives = d.llm_generated?.diag_narratives ?? {};
    const countParts = ['P0', 'P1', 'P2'].map(pr => {
      const n = list.filter(p => p.priority === pr).length;
      return n ? `${pr}×${n}` : '';
    }).filter(Boolean).join(' / ');
    const problems = list.map(p => {
      const meta = PRIORITY_META[p.priority] ?? PRIORITY_META.P2;
      const overrideHtml = clean(diagNarratives[p.code]);
      const bodyHtml = overrideHtml ?? [clean(p.detail) ? `<strong style="color:var(--geo-t);">现象：</strong>${p.detail}` : '', clean(p.root_cause) ? `<strong style="color:var(--geo-t);">根因：</strong>${p.root_cause}` : '', clean(p.action_description) ? `<strong style="color:var(--geo-t);">动作：</strong>${p.action_description}` : ''].filter(Boolean).join('<br>');
      const reached = (p.current_score ?? 0) >= (p.target_score ?? 0);
      return {
        variant: meta.variant,
        pillKind: meta.kind,
        pillLabel: p.priority,
        borderColor: KIND_COLOR[meta.pill] ?? KIND_COLOR.red,
        sourceDimension: p.source_dimension ?? '',
        titleText: p.label ?? '',
        bodyHtml,
        footerNote: `当前 ${p.current_score ?? 0} → 观测线 ${p.target_score ?? 0}${reached ? '（当前值已达观测线，该项转为保持项）' : ''}`
      };
    });
    return {
      subTitle: `共${list.length}项（${countParts}）· P0=立即干预 / P1=本月响应 / P2=持续观察`,
      problems
    };
  });
  // ── CH7.2 问题 × 维度关联 ──
  const problemDimensionLinkProps = computed(() => {
    const list = reportData.value?.problem_diagnosis;
    if (!Array.isArray(list) || !list.length) return undefined;
    const rows = list.map(p => {
      const meta = PRIORITY_META[p.priority] ?? PRIORITY_META.P2;
      const affected = (p.affected_dimensions?.length ? p.affected_dimensions : [p.source_dimension]).filter(Boolean);
      return {
        problem: p.label ?? '',
        priority: p.priority,
        priorityKind: meta.kind,
        affectedDim: affected.join(' + '),
        currentScore: p.current_score ?? 0,
        targetScore: p.target_score ?? 0
      };
    });
    return {
      rows
    };
  });
  // ── CH7.3 消费旅程影响映射 ──
  const journeyImpactProps = computed(() => {
    const stages = reportData.value?.funnel?.stages;
    if (!Array.isArray(stages) || !stages.length) return undefined;
    const rows = stages.map((s, i) => {
      const pct = s.coverage_rate_pct ?? 0;
      return {
        stageHtml: `<strong>${CIRCLED[i] ?? ''} ${s.stage}</strong>`,
        dimension: s.geo_dimension ?? '',
        coverage: `${pct}%`,
        coverageColor: coverageColor(pct),
        perfText: clean(s.perf_text) ?? '—'
      };
    });
    return {
      rows
    };
  });
  // ── CH7.4 根因归纳（problem_diagnosis 直接生成 + 数据佐证现算 + llm 覆盖）──
  const rootCauseProps = computed(() => {
    const d = reportData.value;
    const diags = d?.problem_diagnosis;
    if (!Array.isArray(diags) || !diags.length) return undefined;
    const top1 = top1Competitor.value;
    const llmRc = d.llm_generated?.root_cause_cards ?? [];
    const cards = diags.map((x, i) => {
      const ov = llmRc[i] && typeof llmRc[i] === 'object' ? llmRc[i] : {};
      const meta = PRIORITY_META[x.priority] ?? PRIORITY_META.P2;
      return {
        num: `根因 ${String(i + 1).padStart(2, '0')}`,
        pillLabel: x.priority,
        pillKind: meta.kind,
        code: x.code ?? '',
        accentColor: x.priority === 'P0' ? KIND_COLOR.red : KIND_COLOR.orange,
        title: clean(ov.title) ?? x.label ?? '',
        bodyText: clean(ov.body) ?? x.root_cause ?? '',
        // 数据佐证的拼装规则（维度=当前值·当前→观测线·头部竞品GEO）在组件里实现，这里只给结构化原始字段；
        // ov.evidence 存在时整段由模型覆盖，走 evidenceOverrideHtml
        evidenceDim: x.source_dimension ?? '',
        evidenceScore: x.current_score ?? 0,
        evidenceTarget: x.target_score ?? 0,
        evidenceBenchmarkName: top1?.name,
        evidenceBenchmarkGeo: top1?.GEO,
        evidenceOverrideHtml: clean(ov.evidence) ? `<strong style="color:var(--geo-orange);">数据佐证：</strong>${clean(ov.evidence)}` : undefined
      };
    });
    return {
      subTitle: `${diags.length}类根因 · 每类对应具体数据佐证（N14）`,
      cards
    };
  });
  // ── CH8.1 行动方向（action_roadmap.stages 展开，非固定四方向）──
  const actionDirectionsProps = computed(() => {
    const d = reportData.value;
    const roadmap = d?.action_roadmap?.stages;
    if (!Array.isArray(roadmap) || !roadmap.length) return undefined;
    const titles = d.llm_generated?.direction_card_titles ?? [];
    const bodies = d.llm_generated?.direction_card_bodies ?? [];
    const diags = d.problem_diagnosis ?? [];
    const items = [];
    roadmap.forEach(st => (st.items ?? []).forEach(it => items.push({
      st,
      it
    })));
    const cnNums = '一二三四五六七八九十';
    const directions = items.map(({
      st,
      it
    }, i) => {
      const meta = PRIORITY_META[it.priority] ?? {
        pill: 'gray',
        label: '—'
      };
      const title = clean(titles[i]) ?? it.title;
      const body = clean(bodies[i]) ?? `目标:${it.goal ?? ''} 动作:${(it.actions ?? []).join(';')}`;
      const dimsWatch = Array.from(new Set(`${it.title ?? ''}${it.goal ?? ''}`.match(/BCR|MRR|IA|SS|CWR|SoV/g) ?? []));
      return {
        pillLabel: it.priority ?? '',
        pillKind: meta.pill,
        dims: dimsWatch.length ? dimsWatch.join('/') : diags[0]?.source_dimension ?? '',
        name: `方向${cnNums[i] ?? i + 1}：${title}`,
        bodyHtml: body,
        footerNote: `所属阶段：${st.stage_no} ${st.name}（${st.quarter}）`,
        accent: KIND_COLOR[meta.pill] ?? KIND_COLOR.gray
      };
    });
    return {
      subTitle: `${directions.length}大方向 · 对应P0/P1诊断 · 方向级预览`,
      directions
    };
  });
  // ── CH8.2 推进节奏框架 ──
  const implementationRoadmapProps = computed(() => {
    const d = reportData.value;
    const list = d?.action_roadmap?.stages;
    if (!Array.isArray(list) || !list.length) return undefined;
    const stages = list.map(s => ({
      name: `阶段${s.stage_no} · ${s.name} · ${s.quarter ?? ''}`,
      bodyHtml: (s.items ?? []).map(it => `<div style="font-size:12px;font-weight:600;color:var(--geo-t);margin-bottom:6px;">${it.title ?? ''}</div>${(it.actions ?? []).map(a => `<div style="display:flex;gap:8px;padding:6px 0;font-size:12px;color:var(--geo-tm);line-height:1.7;"><span style="color:var(--geo-gold);">▸</span><div>${a}</div></div>`).join('')}`).join('')
    }));
    const sn = clean(d.llm_generated?.stage_narrative) ?? `<strong>阶段逻辑：</strong>路线图共${list.length}个阶段：${list.map(st => `${st.stage_no}${st.name}(${st.quarter})`).join(' → ')}，按防御→跟进→深化的先后逻辑推进。`;
    return {
      subTitle: `${list.length}个阶段 · 季度排布`,
      stages,
      noteHtml: sn
    };
  });
  // ── CH8.3 为什么GEO是持续运营 ──
  const continuousOperationsProps = computed(() => {
    const d = reportData.value;
    if (!d) return undefined;
    const fr = d.factual_risk ?? {};
    const meta = d.meta ?? {};
    const platformN = (meta.platforms ?? []).length;
    return {
      subTitle: '左：GEO运营闭环机制 · 右：四个不可控外部变量——单次优化必然衰减的原因（N9：模板补回）',
      loopNodes: [{
        title: `对应「GEO监测产品」：${meta.total_keywords ?? 0}词 × ${platformN}平台 × ${meta.monitor_rounds ?? 1}轮全量实测`,
        label: '① 月度全量监测',
        tag: '监测产品'
      }, {
        title: '对应「诊断交付」：P0 / P1 / P2 分级问题清单与根因',
        label: '② 问题诊断分级',
        tag: '诊断交付'
      }, {
        title: '对应「GEO运营服务」：事实口径治理、内容资产建设、平台侧沟通',
        label: '③ 口径治理干预',
        tag: '运营服务'
      }, {
        title: '对应「复测交付」：与基准期逐维对比，验证修复幅度并滚动校准',
        label: '④ 复测验证校准',
        tag: '复测交付'
      }],
      loopFootHtml: '↻ 持续循环 · 随平台模型更新滚动校准。闭环由「监测产品（① ④）＋运营服务（② ③）」共同驱动，与8.4服务衔接对应。',
      variables: [{
        name: '模型持续更新',
        descHtml: '各平台模型版本迭代会重排答案与引用源，单次优化的效果随更新自然衰减。'
      }, {
        name: '语料动态竞争',
        descHtml: '竞品持续产出新内容，推荐位与叙事占位是动态争夺，不是一次性的山头。'
      }, {
        name: '口径漂移风险',
        descHtml: '价格、交付、配置等事实随产品节奏变化，需要持续向各平台同步最新官方口径。'
      }, {
        name: '错误固化成本',
        descHtml: `错误信息渗透训练语料后，纠正成本随时间显著上升——本轮已检出 ${fr.flawed_claims ?? 0}/${fr.total_claims ?? 0} 条断言存在缺陷（6.5）。`
      }]
    };
  });
  // ── CH8.4 服务衔接与交付物边界 ──
  const serviceHandoffProps = computed(() => {
    const d = reportData.value;
    if (!d) return undefined;
    const period = d.meta?.report_date ?? d.meta?.data_period ?? '';
    return {
      handoffHtml: `<strong>服务衔接说明：</strong>本报告为基准期（${period}）诊断快照。后续通过<strong>月度GEO全量复测</strong>跟踪七维指标与问题修复进度，通过<strong>GEO运营服务</strong>执行口径治理、内容建设与平台侧沟通；逐平台操作清单、内容模板、话术规范等执行细则随服务启动在《GEO运营帮助文档》中提供，并跟随平台环境变化持续更新维护。`
    };
  });
  // ── ES 证据支撑度分析（es_analysis 模块，v3 七维新增）──
  const esAnalysisProps = computed(() => {
    const d = reportData.value;
    const ea = d?.es_analysis;
    if (!ea) return undefined;
    const t = d.target_overview ?? {};
    const brand = clean(d.meta?.target_brand) ?? '';
    // 证据质量/降级色值
    const qualityColor = q => ({
      强: 'var(--geo-green)',
      中: 'var(--geo-orange)',
      弱: 'var(--geo-red)',
      缺失: 'var(--geo-td)'
    })[q] ?? 'var(--geo-td)';
    // ES 得分
    const esScore = {
      value: String(t.ES ?? 0),
      label: DIM_META.ES.name,
      weight: `${DIM_META.ES.weight}%`,
      color: scoreColor(t.ES ?? 0)
    };
    // 证据质量卡片
    const qualityCard = {
      current: ea.target_evidence_quality ?? '',
      beforeDegradation: ea.target_quality_before_degradation ?? '',
      degraded: ea.target_quality_degraded ?? false,
      currentColor: qualityColor(ea.target_evidence_quality ?? ''),
      beforeColor: qualityColor(ea.target_quality_before_degradation ?? ''),
      summaryHtml: ea.target_has_evidence ? `${ea.target_records_with_evidence ?? 0}条响应含证据 · 共${ea.target_evidence_count ?? 0}条证据摘录 · 质量评级「${ea.target_evidence_quality}」${ea.target_quality_degraded ? `（降级前「${ea.target_quality_before_degradation}」）` : ''}` : '本轮未检出可溯源证据'
    };
    // 证据类型分布（用于柱状图/标签展示）
    const evidenceTypes = (ea.target_evidence_types ?? []).map(et => ({
      type: et.type ?? '',
      count: et.record_count ?? 0
    }));
    // 证据原文摘录（top N）
    const excerpts = (ea.target_evidence_excerpts ?? []).slice(0, 10).map(text => ({
      text
    }));
    // 监管风险标记（按严重度分组）
    const flagsBySeverity = {};
    (ea.regulatory_flags ?? []).forEach(f => {
      const sev = f.severity ?? '未知';
      flagsBySeverity[sev] = (flagsBySeverity[sev] ?? 0) + 1;
    });
    const regulatorySummary = {
      total: (ea.regulatory_flags ?? []).length,
      high: flagsBySeverity['高'] ?? 0,
      mid: flagsBySeverity['中'] ?? 0,
      low: flagsBySeverity['低'] ?? 0,
      topFlags: (ea.regulatory_flags ?? []).slice(0, 5).map(f => ({
        flagType: f.flag_type_raw ?? f.flag_type ?? '',
        severity: f.severity ?? '',
        evidenceText: String(f.evidence_text ?? '').slice(0, 80),
        note: f.note ?? ''
      }))
    };
    // 合规备注
    const complianceNotes = (ea.compliance_notes ?? []).map(c => ({
      flagType: c.flag_type_raw ?? c.flag_type ?? '',
      severity: c.severity ?? '',
      evidenceText: String(c.evidence_text ?? '').slice(0, 80),
      note: c.note ?? ''
    }));
    // 品牌证据横向对比（本品 + TOP5 竞品）
    const brandComparison = (ea.brand_evidence_comparison ?? []).sort((a, b) => (b.evidence_count ?? 0) - (a.evidence_count ?? 0)).slice(0, 8).map(b => ({
      name: b.name ?? '',
      isTarget: !!b.is_target,
      hasEvidence: b.has_evidence ?? false,
      recordsWithEvidence: b.records_with_evidence ?? 0,
      evidenceCount: b.evidence_count ?? 0,
      quality: b.quality ?? '',
      qualityColor: qualityColor(b.quality ?? ''),
      degraded: b.quality_degraded ?? false,
      highSeverityFlags: b.high_severity_flags ?? 0,
      topExcerpt: String(b.top_excerpt ?? '').slice(0, 100)
    }));
    return {
      esScore,
      qualityCard,
      evidenceTypes,
      excerpts,
      regulatorySummary,
      complianceNotes,
      brandComparison
    };
  });
  // ── CH9.1 GEO 七维指标定义 ──
  const geoDefinitionProps = computed(() => {
    const d = reportData.value;
    if (!d) return undefined;
    const t = d.target_overview ?? {};
    const meta = d.meta ?? {};
    const brand = clean(meta.target_brand) ?? '';
    const defs = sevenDimDefs(brand, cwrInfoOf(t));
    const rows = defs.map(x => ({
      dim: x.dim,
      dimColor: DIM_META[x.dim]?.abbrColor ?? 'var(--geo-blue)',
      name: x.name,
      weight: `${DIM_META[x.dim].weight}%`,
      definition: x.definition,
      formula: x.formula,
      score: t[x.dim] ?? 0
    }));
    const geo = t.GEO ?? 0;
    const grade = t.geo_grade || gradeOf(geo);
    return {
      subTitle: '计算公式 · 测量口径 · 权重设计依据',
      formulaHtml: clean(d.geo_formula?.formula) ?? '',
      headers: ['维度', '权重', '定义', '计算公式', '本轮实测'],
      rows,
      gradeDist: {
        title: '评级分档',
        bandsHtml: clean(d.geo_formula?.grade_rules) ?? '',
        currentHtml: `本轮：${geo}分 → ${grade}`,
        noteHtml: clean(d.category_benchmark?.bcr_note) ?? ''
      }
    };
  });
  // ── CH9.2 竞品说明 ──
  const competitorSpecsProps = computed(() => {
    const d = reportData.value;
    const groups = groupByCompetitorType(d?.brand_stats ?? []);
    if (!groups.length) return undefined;
    const rows = groups.map(g => ({
      type: g.type,
      count: g.names.length,
      brands: `${g.names.slice(0, 5).join('、')}${g.names.length > 5 ? ' 等' : ''}`
    }));
    const total = d?.meta?.total_brands ?? groups.reduce((s, g) => s + g.names.length, 0) + 1;
    return {
      subTitle: `对标集合共${total}个品牌 · 按竞品类型分组`,
      headers: ['类型', '数量', '代表品牌'],
      rows
    };
  });
  // ── CH9.3 数据说明 ──
  const dataSpecsProps = computed(() => {
    const d = reportData.value;
    const meta = d?.meta;
    if (!meta) return undefined;
    const names = platformNames.value;
    return {
      subTitle: '样本量 · 留存 · 时效',
      bodyHtml: `<strong>本轮监测执行量汇总：</strong><br>${meta.total_keywords ?? 0}条咨询词 × ${names.length}个AI平台 × ${meta.monitor_rounds ?? 1}轮 = <strong>${meta.total_answers ?? 0}次AI响应</strong> ✓（获取品牌 ${meta.total_records ?? 0} 个，过滤 ${meta.filtered_records ?? 0} 个）<br>监测时间：${resolveDataPeriod(meta)} · ${meta.monitor_rounds ?? 1}轮<br>数据时效：本报告数据为基准期实测快照，建议按周期进行全量监测更新。<br><strong>原始数据留存：</strong>全部AI响应原文（含平台、触发词、时间戳）、维度评分明细均已归档留存；竞品数据同源同轮、同等留存，支持审计追溯与按需调阅。`
    };
  });
  // ── 报告页脚（新schema无 references/9.4 小节，此计算仅承载宿主级页脚，避免内容丢失）──
  const referencesProps = computed(() => {
    const d = reportData.value;
    if (!d) return undefined;
    const meta = d.meta ?? {};
    const names = platformNames.value;
    return {
      refs: [],
      footer: {
        line1: `${clean(meta.target_brand) ?? ''} · GEO品牌AI可见度监测报告`,
        line2: `监测周期：${resolveDataPeriod(meta)} · ${names.length}个AI平台 · ${meta.total_keywords ?? 0}条咨询词 · ${meta.total_answers ?? 0}次AI响应`,
        line3: '&copy; 2026 Marketingforce（迈富时）版权所有 · 迈富时 GEO 研究团队出品 · 商业机密，未经书面授权不得转载、引用或商用'
      }
    };
  });
  return {
    setReportData,
    meta,
    platforms,
    coverProps,
    compositeScoreProps,
    dimensionBreakdownProps,
    platformCardsProps,
    // CH5
    keywordClassificationProps,
    missingKeywordsProps,
    coverageMatrixProps: dimensionMatrixTableProps,
    journeyCoverageChainProps,
    // CH6 extra
    reputationMonitoringProps,
    citationAnalysisProps,
    // CH9 extra
    referencesProps,
    // CH6
    riskOverviewProps,
    aiQuotesProps,
    accuracyVerificationProps,
    hallucinationRecordsProps,
    // CH2
    monitoringTargetProps,
    categoryCoverageProps,
    narrativeWordAnalysisProps,
    monitoringScopeProps,
    analysisFrameworkProps,
    // CH8
    actionDirectionsProps,
    implementationRoadmapProps,
    continuousOperationsProps,
    serviceHandoffProps,
    esAnalysisProps,
    // CH9
    geoDefinitionProps,
    competitorSpecsProps,
    dataSpecsProps,
    // CH4
    brandRankingProps,
    platformHeatmapProps,
    pkRecordsProps,
    narrativePositioningProps,
    dimensionComparisonProps,
    competitorProfilesProps,
    // CH7
    problemTriageProps,
    problemDimensionLinkProps,
    journeyImpactProps,
    rootCauseProps,
    // CH1
    coreFindingsProps,
    competitorGapProps,
    monitoringConclusionsProps,
    businessImpactProps,
    // 1.2 竞品切换
    selectedCompetitorIdx,
    selectCompetitor
  };
}